// Dubai, "Fountain night at the Burj" (winter). A mild January night on a café terrace at the edge of the Burj Lake
// in Downtown, close to the water, looking across the lake at the Burj Khalifa: a silver needle rising in spiralling
// setbacks (its Y-plan wings step back one after another) into a long spire with red aviation lamps, now and then
// running an LED light show up its facade. Around it: Downtown towers with lit windows and colour-changing crowns,
// twin towers joined by a sky bridge, hazy towers along the highway behind, a rooftop LED hoarding for an invented
// karak tea (the city's billboard), and the metro viaduct with a shell-roofed station where a driverless train stops.
// On the lakeside: a souk-style building with a lit arcade, wind towers and strollers. Twice a loop the fountain
// dances (rippling rows, swaying arcs, tall shooters), lit white and gold and mirrored in the lake, and its mist
// drifts off afterwards; an abra with lanterns glides by. On our terrace: a family and two friends on Sadu cushions
// round a low table with a dallah and cups, sipping qahwa and karak and filming the show; a man smoking the shisha on a
// pouf; a waiter who pours qahwa and changes the coals; brass pierced lanterns throwing dappled light, date palms
// wrapped in fairy lights, a café arch and a cat. Fine desert sand glints in a warm breeze at three depths; a thin
// crescent moon, a plane crossing.
// Beyond the city, low dunes lie hazy on the horizon either side of the skyline: once a loop a camel caravan with a
// lantern walks the far crest, and a campfire flickers on a dune past the café.
import { W, H, LOOP, TAU, frac, clamp, lerp, smooth, wave, step, env, cycle, hash, rng, bayer, hex,
  makeCanvas, rect, ditherFill, makeHalo, drawHalo, reflect, streak, text, textWidth, makePitch } from '../engine/core.js';
import { weatherPlans, drawWeather } from '../engine/weather.js';

let ctx;                                             // the scene's 480x270 context, set in create()
const halo = (hl, x, y, a = 1) => drawHalo(ctx, hl, x, y, a);

/* ================= Layout ================= */
const WL = 178;                                      // far shore waterline = top row of the lake
const FY = 199;                                      // the fountain's nozzle line
const EDGE = 228;                                    // our terrace ledge (the lake's near edge)
const FLOOR = 233;                                   // terrace floor starts below the ledge
const BK = { cx: 240, tip: 7, top: 58 };             // Burj Khalifa: spire tip, top of the stepped mass
const MOON = { x: 152, y: 30 };
const BB = { x: 271, y: 112, w: 50, h: 20 };         // face of the rooftop LED hoarding (exported as `billboard`)
const BBX = { l: BB.x - 2, r: BB.x + BB.w + 1, t: BB.y - 2, b: BB.y + BB.h + 1 };   // its steel casing
const BB_BLD = { x0: 264, x1: 328, top: 141 };       // the mid-rise it stands on
const VIA = 157;                                     // metro viaduct deck, top row
const STN = { x0: 336, x1: 392 };                    // the shell-roofed metro station
const SOUK = { x0: 6, x1: 178, roof: 148 };          // souk-style building on the lakeside (left)
const CAFE = { x: 420, top: 146 };                   // our café's wall and arch (right)
const PALM_L = { bx: 30, by: 250, tx: 40, ty: 44 };  // date palms: trunk base and crown
const PALM_R = { bx: 411, by: 250, tx: 416, ty: 50 };

/* ================= Small pixel helpers ================= */
const RGB = {};
const C = s => RGB[s] || (RGB[s] = hex(s));
function pix(w, h) {
  const [c, x] = makeCanvas(w, h), img = x.createImageData(w, h), d = img.data;
  return {
    c, x, w, h, d,
    set(i, j, col, a = 255) {
      i = Math.round(i); j = Math.round(j);
      if (i < 0 || j < 0 || i >= w || j >= h) return;
      const o = (j * w + i) * 4, v = typeof col === 'string' ? C(col) : col;
      d[o] = v[0]; d[o + 1] = v[1]; d[o + 2] = v[2]; d[o + 3] = a;
    },
    alpha(i, j) { return (i < 0 || j < 0 || i >= w || j >= h) ? 0 : d[(j * w + i) * 4 + 3]; },
    fill(i, j, ww, hh, col) { for (let y = j; y < j + hh; y++) for (let k = i; k < i + ww; k++) this.set(k, y, col); },
    done() { x.putImageData(img, 0, 0); return c; },
  };
}
/** ordered-dithered pick from a colour ramp, v in 0..1 */
function pick(ramp, v, i, j) {
  const f = clamp(v, 0, 1) * (ramp.length - 1);
  let k = Math.floor(f);
  if (f - k > bayer(i, j)) k++;
  return ramp[Math.min(k, ramp.length - 1)];
}
/** smooth value noise in 0..1 (build time only) */
function vnoise(x, y, s) {
  const xi = Math.floor(x), yi = Math.floor(y), fx = x - xi, fy = y - yi;
  const a = hash(xi, yi, s), b = hash(xi + 1, yi, s), c = hash(xi, yi + 1, s), d = hash(xi + 1, yi + 1, s);
  const u = fx * fx * (3 - 2 * fx), v = fy * fy * (3 - 2 * fy);
  return lerp(lerp(a, b, u), lerp(c, d, u), v);
}
const toHex = (r, g, b) => '#' + ((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1);
const mixHex = (a, b, t) => { const A = hex(a), B = hex(b); return toHex(...A.map((v, i) => Math.round(lerp(v, B[i], t)))); };
const dim = (col, k) => { const c = hex(col); return [Math.round(c[0] * k), Math.round(c[1] * k), Math.round(c[2] * k)]; };
/** crop a pix buffer to its opaque bounding box (build time); draw the result with blit() */
function trim(p) {
  let x0 = p.w, y0 = p.h, x1 = -1, y1 = -1;
  for (let y = 0; y < p.h; y++) for (let i = 0; i < p.w; i++) if (p.d[(y * p.w + i) * 4 + 3]) {
    if (i < x0) x0 = i; if (i > x1) x1 = i; if (y < y0) y0 = y; if (y > y1) y1 = y;
  }
  const full = p.done();
  if (x1 < 0) return { c: makeCanvas(1, 1)[0], x: 0, y: 0 };
  const [c, x] = makeCanvas(x1 - x0 + 1, y1 - y0 + 1);
  x.drawImage(full, -x0, -y0);
  return { c, x: x0, y: y0 };
}
const blit = o => ctx.drawImage(o.c, o.x, o.y);
/** sprite canvas from rows of characters and a colour key ('.' = transparent) */
function sprite(rows, key) {
  const h = rows.length, w = Math.max(...rows.map(r => r.length)), p = pix(w, h);
  rows.forEach((row, y) => { for (let i = 0; i < row.length; i++) { const col = key[row[i]]; if (col) p.set(i, y, col); } });
  return p.done();
}
const flipRows = rows => rows.map(r => [...r].reverse().join(''));
function lineP(p, x0, y0, x1, y1, col) {             // 1 px line into a pix buffer, integer endpoints
  const n = Math.max(Math.abs(x1 - x0), Math.abs(y1 - y0), 1);
  for (let k = 0; k <= n; k++) p.set(x0 + (x1 - x0) * k / n, y0 + (y1 - y0) * k / n, col);
}

/* ================= Sky: violet desert night, the city's glow warming the dust near the horizon ================= */
const SKY = ['#05041a', '#08061f', '#0c0925', '#110c2c', '#160f33', '#1c1339', '#23173f', '#2b1b45', '#341f4a',
  '#3e244e', '#492a51', '#553052', '#613752', '#6e3f52', '#7b4852', '#885252', '#955d54'];
function renderSky() {
  const p = pix(W, H);
  for (let y = 0; y < H; y++) for (let i = 0; i < W; i++) {
    let v = Math.pow(Math.min(1, y / 192), 1.25) * 0.72;
    v += Math.max(0, 1 - Math.hypot((i - BK.cx) / 320, (y - 190) / 118)) * 0.34;   // Downtown's light dome in the dust
    v += Math.max(0, 1 - Math.hypot((i - MOON.x) / 46, (y - MOON.y) / 30)) * 0.05;  // moon haze
    p.set(i, y, pick(SKY, v, i, y));
  }
  return p.done();
}
const STARS = [];
{ const r = rng(21); while (STARS.length < 18) { const x = Math.floor(r() * W), y = 3 + Math.floor(r() * 68); if (Math.abs(x - BK.cx) < 4) continue; STARS.push({ x, y, k: 20 + Math.floor(r() * 40), ph: r(), b: 0.2 + r() * 0.4 }); } }
/** a thin crescent lying on its back (the Gulf's low latitude tips it over), faint earthshine on the rest */
function renderMoon() {
  const s = 13, p = pix(s, s);
  for (let y = 0; y < s; y++) for (let i = 0; i < s; i++) {
    const d1 = Math.hypot(i - 6, y - 6), d2 = Math.hypot(i - 6.9, y - 3.6);
    if (d1 > 5.3) continue;
    if (d2 > 5.1) p.set(i, y, d2 > 6.1 && d1 < 4.6 ? '#fff4d0' : d1 > 4.6 ? '#d8c49a' : '#f4e2b4');
    else p.set(i, y, '#241c3c', 150);
  }
  return p.done();
}

/* ================= Desert haze: thin dusty bands drifting over the city (static canvases, scrolled) ================= */
const HAZE_DEFS = [
  { seed: 3, y: 96, h: 48, k: 1, dir: 1, top: '#3a2a4c', bot: '#5a3c52', alpha: 0.5 },
  { seed: 9, y: 134, h: 46, k: 2, dir: -1, top: '#5a3c52', bot: '#86585a', alpha: 0.55 },
  { seed: 15, y: 40, h: 40, k: 1, dir: -1, top: '#1c1434', bot: '#2c1e40', alpha: 0.5 },
];
function makeHaze(def) {
  const h = def.h, A = hex(def.top), B = hex(def.bot), [c, cx] = makeCanvas(W, h), img = cx.createImageData(W, h), d = img.data;
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    const v = y / (h - 1), prof = Math.pow(Math.sin(Math.PI * Math.pow(v, 0.9)), 1.8);
    const nz = 0.45 + 0.3 * Math.sin(TAU * 2 * x / W + def.seed + y * 0.06) + 0.18 * Math.sin(TAU * 5 * x / W - y * 0.21 + def.seed * 2)
      + 0.1 * Math.sin(TAU * 13 * x / W + y * 0.37);
    const q = Math.floor(clamp(prof * nz, 0, 1) * 4 + bayer(x, y)) / 4;
    if (q <= 0) continue;
    const o = (y * W + x) * 4;
    d[o] = A[0] + (B[0] - A[0]) * v; d[o + 1] = A[1] + (B[1] - A[1]) * v; d[o + 2] = A[2] + (B[2] - A[2]) * v;
    d[o + 3] = Math.round(Math.min(1, q) * 255);
  }
  cx.putImageData(img, 0, 0);
  return { ...def, c };
}
function drawScroll(cv, y, k, t, dir = 1) {
  let off = Math.floor(frac(k * t / LOOP) * W);
  if (dir < 0) off = (W - off) % W;
  ctx.drawImage(cv, off - W, y); ctx.drawImage(cv, off, y);
}
function drawHaze(hz, t, a = 1) {
  ctx.globalAlpha = hz.alpha * a * (0.8 + 0.2 * wave(t, 3, hz.seed / 10));
  drawScroll(hz.c, hz.y, hz.k, t, hz.dir);
  ctx.globalAlpha = 1;
}

/* ================= An airliner crossing high over Downtown (behind the Burj's spire) ================= */
const PLANE = { a: 100, b: 170, x0: W + 14, x1: -16, y0: 41, y1: 33 };
function drawPlane(t) {
  if (t < PLANE.a || t >= PLANE.b) return;
  const s = (t - PLANE.a) / (PLANE.b - PLANE.a), x = Math.round(lerp(PLANE.x0, PLANE.x1, s)), y = Math.round(lerp(PLANE.y0, PLANE.y1, s));
  rect(ctx, x - 3, y, 8, 1, '#1a1628'); rect(ctx, x, y + 1, 2, 1, '#1a1628'); rect(ctx, x + 4, y - 1, 1, 1, '#1a1628');
  rect(ctx, x - 3, y, 1, 1, '#4a4460');
  if (frac(t / 1.3) < 0.5) { rect(ctx, x + 1, y + 1, 1, 1, '#ff3a3a'); halo(HL.red, x + 1, y + 1, 0.5); }   // belly beacon
  rect(ctx, x, y + 1, 1, 1, frac(t / 1.3 + 0.5) < 0.5 ? '#3aff7a' : '#1a5a30');                           // wingtip
  const f = frac(t / 1.6);
  if (f < 0.05 || (f > 0.1 && f < 0.15)) { rect(ctx, x + 4, y - 1, 1, 1, '#ffffff'); halo(HL.strobe, x + 4, y - 1, 0.9); }   // double strobe
}

/* ================= Building layers: a static canvas + a canvas of lit windows toggled per floor segment ================= */
const WARM = ['#ffd28a', '#ffc574', '#ffe0a8', '#f0b060'];
const COOL = ['#e0ecff', '#c8dcff', '#f0f6ff', '#b8d0f8'];
function makeLayer() { return { p: pix(W, H), lit: pix(W, H), segs: [], blinks: [] }; }
function finishLayer(L) { return { ...trim(L.p), lit: L.lit.done(), segs: L.segs, blinks: L.blinks }; }
/**
 * Floor rows of windows between columns x0..x1 on row y. Some runs are baked lit or dark, the rest
 * become toggling segments (drawn from the lit canvas when their hash says "on").
 */
function floorRow(L, x0, x1, y, r, o = {}) {
  const { warm = 0.5, toggle = 0.4, on = 0.5, pitch = 2, dark = '#0c0e1c', min = 3, max = 9, k = 0.9 } = o;
  let x = x0;
  while (x <= x1) {
    const len = Math.min(x1 - x + 1, min + Math.floor(r() * (max - min + 1)));
    const pal = r() < warm ? WARM : COOL, col = pal[Math.floor(r() * pal.length)];
    const tg = r() < toggle, lit = !tg && r() < on;
    for (let q = 0; q < len; q += pitch) {
      L.p.set(x + q, y, lit ? dim(col, k) : dark);
      if (tg) L.lit.set(x + q, y, dim(col, k));
    }
    if (tg) L.segs.push({ id: L.segs.length, x, y, w: len, n: 2 + Math.floor(r() * 7), p: 0.3 + r() * 0.45, off: r() });
    x += len;
  }
}
function drawLayer(L, t, salt) {
  blit(L);
  let rx = 0, ry = 0, rw = 0;                          // runs of adjacent lit segments are copied in one go
  for (const s of L.segs) {
    if (hash(s.id + salt, Math.floor(frac(t / LOOP + s.off) * s.n), 4) >= s.p) continue;
    if (rw && s.y === ry && s.x === rx + rw) { rw += s.w; continue; }
    if (rw) ctx.drawImage(L.lit, rx, ry, rw, 1, rx, ry, rw, 1);
    rx = s.x; ry = s.y; rw = s.w;
  }
  if (rw) ctx.drawImage(L.lit, rx, ry, rw, 1, rx, ry, rw, 1);
}
const nearBurj = (x0, x1) => x1 > BK.cx - 22 && x0 < BK.cx + 22;

/* ================= Far towers along the highway behind Downtown, softened by the dust ================= */
function renderFar() {
  const L = makeLayer(), p = L.p, r = rng(41), list = [];
  for (let x = -4; x < W;) {
    const bw = 5 + Math.floor(r() * 9);
    let top = 126 + Math.floor(r() * 34);
    if (nearBurj(x, x + bw)) top = Math.max(top, 150);
    list.push({ x, w: bw, top, crown: r() < 0.18 ? 'spire' : r() < 0.3 ? 'step' : 'flat' });
    x += bw + (r() < 0.3 ? 1 + Math.floor(r() * 3) : 0);
  }
  // a pair of triangular-topped towers down the highway, and a few taller ones in the dust
  list.push({ x: 434, w: 9, top: 104, crown: 'tri' }, { x: 447, w: 8, top: 111, crown: 'tri' }, { x: 98, w: 9, top: 114, crown: 'spire' },
    { x: 126, w: 8, top: 120, crown: 'step' }, { x: 300, w: 9, top: 118, crown: 'spire' }, { x: 60, w: 10, top: 120, crown: 'flat' });
  for (const b of list) {
    const c = (b.w - 1) / 2;
    const topAt = i => b.crown === 'tri' ? b.top + Math.round(Math.abs(i - c) * 1.7)
      : b.crown === 'step' ? b.top + (i < 2 || i >= b.w - 2 ? 3 : 0)
        : b.crown === 'spire' ? b.top + Math.round(Math.max(0, Math.abs(i - c) - 1) * 1.2) : b.top;
    const tone = r() < 0.5 ? '#221a36' : '#261d3a';
    for (let i = 0; i < b.w; i++) for (let y = topAt(i); y < WL; y++)
      p.set(b.x + i, y, y === topAt(i) ? '#3a2e4e' : i === 0 ? '#2e2446' : i === b.w - 1 ? '#1a1430' : tone);
    if (b.crown === 'spire') { const sx = b.x + Math.round(c); for (let y = b.top - 7; y < b.top; y++) p.set(sx, y, '#2e2446'); L.blinks.push([sx, b.top - 8]); }
    if (b.crown === 'tri') L.blinks.push([b.x + Math.round(c), b.top - 1]);
    for (let y = topAt(0) + 4; y < WL - 2; y += 3) for (let i = 1; i < b.w - 1; i += 2) {
      const v = r();
      if (v < 0.22) p.set(b.x + i, y, r() < 0.6 ? '#7a5a5a' : '#5a5a7a');
      else if (v < 0.3) { L.lit.set(b.x + i, y, r() < 0.6 ? '#b88a70' : '#8a92b8'); L.segs.push({ id: L.segs.length, x: b.x + i, y, w: 1, n: 2 + Math.floor(r() * 5), p: 0.5, off: r() }); }
    }
  }
  return finishLayer(L);
}

/* ================= The desert beyond the city: low dune lines on the far horizon, softened by the dust =================
   Two far ridges of long, rounded swells, barely higher than the far towers: they show only beside the skyline (over the
   souk's wind towers and past the café) and sink out of sight behind Downtown. At this distance the sand is dusk-violet
   haze with a faint moonlit rim on the slopes turned to the moon (up to the left), and a thin dust lies along every
   crest. Pre-rendered in two canvases, the far ridge and then the near one: the caravan walks the far crest.
   swells: [x, height above the ridge's floor, windward half-width, lee half-width]. */
const DUNES = [
  { floor: [172, 127], lift: [[60, 150], [80, 190]],                  // far: the caravan's ridge
    swells: [[-30, 7, 64, 48], [70, 9, 74, 56], [150, 5, 44, 34], [428, 6, 52, 40], [500, 8, 60, 40]],
    col: { S: '#21183a', F: '#291d40', L: '#3a2a4a', rim: '#7a6070' } },
  { floor: [176, 136], lift: [[90, 170], [100, 200]],
    swells: [[14, 6, 38, 28], [104, 5, 42, 30], [452, 6, 36, 26]],
    col: { S: '#1d1534', F: '#231a3a', L: '#34264a', rim: '#6a5268' } },
];
const DUNE_HAZE = '#4a3a5c';
const duneLift = (x, D) => { const [a, b] = D.lift[x < BK.cx ? 0 : 1]; return smooth(a, b, Math.abs(x - BK.cx)); };   // 0 behind the Burj, 1 beside the skyline
/** a ridge's crest row at column x: its floor plus the highest swell there */
function duneY(D, x) {
  const lift = duneLift(x, D);
  let up = 0;
  for (const [px, hgt, wl, wr] of D.swells) {
    const dx = x - px, w = dx < 0 ? wl : wr;
    if (Math.abs(dx) < w) up = Math.max(up, hgt * (1 + Math.cos(Math.PI * dx / w)) / 2);
  }
  return lerp(D.floor[0], D.floor[1], lift) - up * lift;
}
const duneCrest = (D, x) => Math.round(duneY(D, x));
function renderDunes(list) {
  const p = pix(W, WL);
  for (const D of list) {
    const K = D.col;
    for (let x = 0; x < W; x++) {
      const c = duneCrest(D, x), lit = duneY(D, x + 1) < duneY(D, x - 1) - 0.08;   // the slope rises to the right: it faces the moon
      if (c >= WL - 1) continue;
      for (let k = 1; k <= 2; k++) if (0.5 / k > bayer(x, c - k)) p.set(x, c - k, DUNE_HAZE, 70);   // dust along the crest
      for (let y = c; y < WL; y++) {
        const d = y - c, b = bayer(x, y);
        p.set(x, y, d === 0 ? (lit ? K.rim : K.L) : lit && smooth(1, 4, d) < b ? K.L : smooth(2, 9, d) > b ? K.S : K.F);
      }
    }
  }
  return trim(p);
}

/* a camel caravan walks the far crest once a loop, between the fountain shows: out from behind Downtown, past the souk's
   wind towers and off the left edge, a rider with a lantern on the lead camel. Four camels, nose to tail. */
const CARAVAN = { a: 76, b: 158, x0: 178, x1: -10, n: 4, gap: 10 };
CARAVAN.v = (CARAVAN.x0 - CARAVAN.x1 + (CARAVAN.n - 1) * CARAVAN.gap) / (CARAVAN.b - CARAVAN.a);
const CAMEL_TOP = ['....#....', '...##....', '...##..##', '..####.#.', '.#######.', '########.', '.##...##.'];   // facing right, rows 0-1 the rider
const CAMEL_LEGS = [['.#.....#.', '.#.....#.'], ['#..#.#..#', '#..#.#..#']];                               // legs passing / striding
const CAMEL_LANTERN = [3, 1];                        // in the left-facing sprite: held out in front of the rider
/** a camel sprite facing left: a dark silhouette, its top edges caught by the moon */
function makeCamel(rider, legs) {
  const rows = flipRows([...CAMEL_TOP, ...legs].slice(rider ? 0 : 2)), h = rows.length, w = rows[0].length, p = pix(w, h);
  const on = (i, j) => j >= 0 && j < h && i >= 0 && i < w && rows[j][i] === '#';
  for (let j = 0; j < h; j++) for (let i = 0; i < w; i++) if (on(i, j)) p.set(i, j, !on(i, j - 1) && j < h - 3 ? '#b8946c' : '#16101c');
  return p.done();
}
function drawCaravan(t) {
  const { a, b, x0, x1, n, gap, v } = CARAVAN;
  if (t < a || t >= b) return;
  const s = step(t, 480);
  for (let i = n - 1; i >= 0; i--) {
    const x = Math.round(x0 - v * (t - a) + i * gap);
    if (x > x0 || x < x1) continue;                  // not yet out from behind Downtown / gone off the edge
    const spr = CAMEL_C[i === 0 || i === 2 ? 0 : 1][(s + i) & 1], y = duneCrest(DUNES[0], x + 4) - spr.height;
    ctx.drawImage(spr, x, y);
    if (i === 0) {                                   // the lead rider's lantern
      const f = 0.82 + 0.18 * hash(3, step(t, 1440), 9), [lx, ly] = CAMEL_LANTERN;
      halo(HL.caravan, x + lx, y + ly, f);
      rect(ctx, x + lx, y + ly, 1, 1, f > 0.9 ? '#fff0b8' : '#ffc860');
    }
  }
}
/* a far campfire on a dune past the café: a flickering warm point, its glow on the sand, a thin wisp of smoke */
const CAMP = { x: 461, y: duneCrest(DUNES[1], 461) - 1 };
function drawCamp(t, gust) {
  const { x, y } = CAMP, s = step(t, 1440), f = 0.78 + 0.14 * hash(1, s, 7) + 0.08 * wave(t, 37, 0.3);
  halo(HL.camp, x, y - 1, f);
  halo(HL.campCore, x, y - 1, f);
  rect(ctx, x - 1, y + 1, 3, 1, '#4a2014');                                                    // the embers under it
  rect(ctx, x - 1, y, 3, 1, '#ff8a30'); rect(ctx, x, y, 1, 1, f > 0.84 ? '#fff4b0' : '#ffd070');
  rect(ctx, x, y - 1, 1, 1, '#ffc050');
  if (hash(2, s, 7) < 0.5) rect(ctx, x + (hash(3, s, 7) < 0.5 ? -1 : 1), y - 1, 1, 1, '#ff7a28');   // flames licking up
  if (hash(4, s, 7) < 0.7) rect(ctx, x + (hash(5, s, 7) < 0.3 ? -1 : hash(5, s, 7) > 0.7 ? 1 : 0), y - 2, 1, 1, '#ff6420');
  ctx.fillStyle = '#9a8aa0';
  for (let k = 0; k < 6; k++) {                     // smoke, leaning off with the breeze
    const u = frac(t * (20 + k * 3) / LOOP + k / 6), X = Math.round(x - u * (5 + gust * 4) + wave(t, 44 + k * 5, k * 0.2) * u * 1.2);
    ctx.globalAlpha = 0.3 * Math.min(1, u * 5) * (1 - u);
    ctx.fillRect(X, Math.round(y - 4 - u * 14), 1, 1);
  }
  ctx.globalAlpha = 1;
}
function drawDunes(t, gust) {
  blit(DUNE_BACK);
  drawCaravan(t);
  blit(DUNE_FRONT);
  drawCamp(t, gust);
}

/* ================= Downtown towers around the Burj: lit windows, colour-changing crowns, twin towers and a sky bridge ================= */
const CROWNS = [];                                   // LED crown strips, recoloured per frame: { x, y, w, ph, k }
const TOWERS = [
  { x: 178, w: 16, top: 92, crown: 'slope' }, { x: 158, w: 13, top: 118, crown: 'ring' }, { x: 196, w: 11, top: 110, crown: 'step' },
  { x: 208, w: 10, top: 132, crown: 'flat' }, { x: 140, w: 12, top: 128, crown: 'step' },
  { x: BB_BLD.x0, w: BB_BLD.x1 - BB_BLD.x0 + 1, top: BB_BLD.top, crown: 'roof' },
  { x: 332, w: 12, top: 100, crown: 'twin' }, { x: 350, w: 12, top: 93, crown: 'twin' },
  { x: 368, w: 13, top: 84, crown: 'spire' }, { x: 386, w: 12, top: 108, crown: 'ring' }, { x: 400, w: 16, top: 122, crown: 'flat' },
];
const BRIDGE = { x0: 344, x1: 349, y: 103 };         // the twin towers' sky bridge
function renderTowers() {
  const L = makeLayer(), p = L.p, r = rng(47), list = [];
  for (let x = 128; x < 432;) {
    const bw = 6 + Math.floor(r() * 9);
    let top = 138 + Math.floor(r() * 28);
    if (nearBurj(x, x + bw)) top = Math.max(top, 162);
    if (!TOWERS.some(f => x < f.x + f.w && x + bw > f.x)) list.push({ x, w: bw, top, crown: r() < 0.3 ? 'step' : 'flat', filler: true });
    x += bw + (r() < 0.25 ? 2 : 0);
  }
  list.push(...TOWERS);
  for (const b of list) {
    const c = (b.w - 1) / 2;
    const topAt = i => {
      switch (b.crown) {
        case 'slope': return b.top + Math.round((b.w - 1 - i) * 0.85);
        case 'step': return b.top + (i < 2 || i >= b.w - 2 ? 4 : 0);
        case 'spire': return b.top + Math.round(Math.max(0, Math.abs(i - c) - 1) * 1.5);
        default: return b.top;
      }
    };
    const tone = b.filler ? (r() < 0.5 ? '#141630' : '#171a34') : (r() < 0.5 ? '#161a34' : '#1a1e3a');
    for (let i = 0; i < b.w; i++) {
      const t0 = topAt(i);
      for (let y = t0; y < WL; y++) {
        let col = i === 0 ? '#2a2e4e' : i === b.w - 1 ? '#0c0e1e' : (i === 1 && !b.filler ? '#22264a' : tone);
        if (y === t0) col = '#3a4064';
        p.set(b.x + i, y, col);
      }
    }
    let y0 = b.top + 4;
    if (b.crown === 'slope') {                         // sloped roof lit by a gold LED edge, a needle at its high corner
      for (let i = 0; i < b.w; i++) p.set(b.x + i, topAt(i), '#ffd890');
      for (let y = b.top - 14; y < b.top; y++) p.set(b.x + b.w - 2, y, y & 1 ? '#8a8aa8' : '#c8c8e0');
      L.blinks.push([b.x + b.w - 2, b.top - 15]);
      y0 = b.top + b.w + 2;
      for (let y = y0; y < 170; y++) if (y % 2) p.set(b.x + 3, y, '#6a7ab8');     // a lit vertical fin
    } else if (b.crown === 'ring') {
      CROWNS.push({ x: b.x + 1, y: b.top + 2, w: b.w - 2, ph: r(), k: 3 + Math.floor(r() * 3) });
      p.fill(b.x + 1, b.top + 2, b.w - 2, 2, '#2a3050');
      for (let y = b.top - 6; y < b.top; y++) p.set(b.x + Math.round(c), y, '#3a4064');
      L.blinks.push([b.x + Math.round(c), b.top - 7]);
      y0 = b.top + 7;
    } else if (b.crown === 'twin') {
      CROWNS.push({ x: b.x + 1, y: b.top + 1, w: b.w - 2, ph: 0.5, k: 4, twin: true });
      L.blinks.push([b.x + 1, b.top - 1]);
      y0 = b.top + 5;
    } else if (b.crown === 'spire') {
      for (let y = b.top - 13; y < b.top; y++) p.set(b.x + Math.round(c), y, y & 1 ? '#8a8aa8' : '#c8c8e0');
      for (let i = 0; i < b.w; i++) p.set(b.x + i, topAt(i), '#bfe0ff');
      L.blinks.push([b.x + Math.round(c), b.top - 14]);
      y0 = b.top + 8;
    } else if (b.crown === 'roof') {                   // the hoarding's mid-rise: parapet, a plant room
      p.fill(b.x + 44, b.top - 4, 10, 4, '#1c2040'); p.fill(b.x + 44, b.top - 4, 10, 1, '#343a60');
      y0 = b.top + 4;
    } else if (!b.filler && r() < 0.6) L.blinks.push([b.x + 1, topAt(1) - 1]);
    const dense = !b.filler && b.crown !== 'roof';
    for (let y = y0; y < 176; y += dense ? 2 : 3)
      floorRow(L, b.x + 1, b.x + b.w - 2, y, r, dense ? { warm: 0.35, toggle: 0.3, on: 0.5, k: 0.85 } : { warm: 0.55, toggle: 0.3, on: 0.4 });
  }
  // the sky bridge between the twins
  for (let x = BRIDGE.x0; x <= BRIDGE.x1; x++) { p.set(x, BRIDGE.y, '#8a92c0'); p.set(x, BRIDGE.y + 1, '#cfe6ff'); p.set(x, BRIDGE.y + 2, '#a8c8f0'); p.set(x, BRIDGE.y + 3, '#2a3050'); }
  return finishLayer(L);
}

/* ================= The Burj Khalifa: a bundle of wings stepping back in a spiral, a pinnacle, a long spire =================
   Seen across the lake its Y-plan reads as strands of different heights: the core runs to the top, the wings end one
   after another, alternating left and right. Each wing's top is chamfered and carries a line of setback lights. */
const BK_STRANDS = [[-2, 2, 58], [3, 4, 70], [-4, -3, 78], [5, 6, 92], [-6, -5, 101], [7, 8, 111], [-8, -7, 118],
  [9, 10, 127], [-10, -9, 133], [11, 12, 140], [-12, -11, 145], [13, 14, 151], [-14, -13, 155]];
const bkStrand = dx => BK_STRANDS.find(([a, b]) => dx >= a && dx <= b);
function bkTop(dx) {                                  // first row of the stepped mass in column dx (999: none)
  const s = bkStrand(dx);
  if (!s) return 999;
  const [a, b, top] = s;
  if (a === -2) return top + (Math.abs(dx) === 2 ? 1 : 0);
  return top + ((a < 0 ? dx === a : dx === b) ? 2 : 0);
}
const BK_M = { x: BK.cx - 14, y: 45, w: 29, h: WL - 45 };   // the LED facade's box (mass + pinnacle)
const BK_RAMP = ['#0e1228', '#141a32', '#1c243e', '#26304e', '#313c5e', '#3e4b72', '#505f88', '#6778a0', '#8496bc', '#a8b8da', '#ccd8f0'];
const BK_BANDS = [81, 105, 129, 149];                 // mechanical floors: darker louvred bands
const BK_SPARK = [];                                  // facade pixels that sparkle now and then
function renderBurj() {
  const L = makeLayer(), p = L.p, cx = BK.cx, r = rng(83), m = pix(BK_M.w, BK_M.h);
  for (let dx = -14; dx <= 14; dx++) {
    const top = bkTop(dx), [a, b] = bkStrand(dx);
    for (let y = top; y < WL; y++) {
      const x = cx + dx;
      // lit from the left, the front wing's ridge (dx -1) catching the light, the top floors floodlit
      let v = 0.36 - dx * 0.012 + (dx < -1 ? 0.1 : dx === -1 ? 0.34 : dx === 0 ? 0.02 : -0.06);
      v += 0.22 * smooth(112, 60, y) + (vnoise(dx / 2, y / 9, 5) - 0.5) * 0.08;
      if (y % 3 === 0) v -= 0.06;
      if (BK_BANDS.includes(y)) v -= 0.16;
      let col = pick(BK_RAMP, v, x, y);
      const edgeL = bkTop(dx - 1) > y, edgeR = bkTop(dx + 1) > y;
      if (edgeL) col = y < 120 ? '#b8c8e8' : '#8a9cc4';
      else if (edgeR) col = '#0a0e1c';
      if (y === top) col = edgeR ? '#8a9ac0' : '#eef4ff';               // setback lights along each wing's top
      else if (y === top + 1 && !edgeL && !edgeR) col = '#8ea0c8';
      p.set(x, y, col);
      m.set(dx + 14, y - BK_M.y, [255, 255, 255], y % 2 ? 190 : 255);
      if (y > top + 2 && !edgeL && !edgeR && r() < 0.05) BK_SPARK.push([x, y]);
    }
    // a wing's top casts a thin shadow groove down its inner neighbour
    if (a !== -2) { const inner = a < 0 ? b + 1 : a - 1; for (let y = top + 1; y < top + 9; y++) if (bkTop(inner) < y && y % 2) p.set(cx + inner, y, '#1a2038'); }
  }
  // windows: a sparse sparkle of lit floors, some toggling
  for (let y = 62; y < WL - 2; y += 3) for (let dx = -13; dx <= 13; dx++) {
    if (bkTop(dx) + 2 >= y || bkTop(dx - 1) > y || bkTop(dx + 1) > y || r() > 0.16) continue;
    const col = r() < 0.6 ? COOL[Math.floor(r() * 4)] : WARM[Math.floor(r() * 4)];
    if (r() < 0.5) { p.set(cx + dx, y, dim(col, 0.8)); continue; }
    L.lit.set(cx + dx, y, col);
    L.segs.push({ id: L.segs.length, x: cx + dx, y, w: 1, n: 2 + Math.floor(r() * 6), p: 0.5, off: r() });
  }
  // pinnacle: a narrow lit crown with a lattice rhythm above the core
  for (let y = 47; y < 58; y++) for (let dx = -1; dx <= 1; dx++) {
    p.set(cx + dx, y, dx < 0 ? (y & 1 ? '#c8d4f0' : '#9aa8cc') : dx === 0 ? (y & 1 ? '#e8f0ff' : '#aab8da') : (y & 1 ? '#56648a' : '#3a4668'));
    m.set(dx + 14, y - BK_M.y, [255, 255, 255], 255);
  }
  for (const dx of [-1, 0, 1]) { p.set(cx + dx, 46, '#f4f8ff'); m.set(dx + 14, 1, [255, 255, 255], 255); }
  // the spire: a silver needle with ring marks
  for (let y = BK.tip; y < 46; y++) p.set(cx, y, y < 12 ? '#f0f4ff' : y & 1 ? '#c8d0e4' : '#8a94b0');
  for (const y of [22, 33, 41]) { p.set(cx - 1, y, '#5a6484'); p.set(cx + 1, y, '#3a4262'); }
  for (let y = 38; y < 46; y++) { p.set(cx - 1, y, '#7a86a8'); p.set(cx + 1, y, '#3e4868'); }
  L.blinks.push([cx, BK.tip - 1], [cx, 21], [cx, 32], [cx - 1, 44], [cx + 1, 44], [cx - 4, 77], [cx + 4, 69], [cx + 8, 110]);
  return { ...finishLayer(L), mask: m.done() };
}

/* ================= Metro viaduct: concrete deck on T-piers, a shell-roofed station (back half; the front glass is per frame) ================= */
const PIERS = [];
for (let x = 196; x < 420; x += 40) PIERS.push(x);
const STN_MID = (STN.x0 + STN.x1) / 2, STN_HW = (STN.x1 - STN.x0) / 2;
const stnRoof = x => Math.round(VIA - 16 + 10 * ((x - STN_MID) / STN_HW) ** 2);
function renderViaduct() {
  const p = pix(W, H);
  for (let x = SOUK.x1 - 4; x < CAFE.x + 4; x++) {
    p.set(x, VIA - 1, x % 3 === 0 ? '#4a4460' : '#3a3450');                   // parapet
    p.set(x, VIA, '#9a92aa'); p.set(x, VIA + 1, '#6a6280'); p.set(x, VIA + 2, '#48425c'); p.set(x, VIA + 3, '#241f34');
    if (x % 12 === 5) p.set(x, VIA + 3, '#c8905a');                           // lamps under the deck
  }
  for (const px0 of PIERS) {
    for (let dx = -4; dx <= 4; dx++) p.set(px0 + dx, VIA + 4, dx < -2 ? '#5a5470' : '#3a3450');
    for (let dx = -3; dx <= 3; dx++) p.set(px0 + dx, VIA + 5, dx < -1 ? '#4a4462' : '#2e2a42');
    for (let y = VIA + 6; y < WL; y++) { p.set(px0 - 1, y, '#4a4462'); p.set(px0, y, '#34304a'); p.set(px0 + 1, y, '#221e34'); }
  }
  // station: glass walls lit cool behind the tracks, a champagne shell roof arching over them
  for (let x = STN.x0 + 3; x <= STN.x1 - 3; x++) for (let y = VIA - 9; y < VIA - 1; y++)
    p.set(x, y, (x - STN.x0) % 5 === 0 ? '#4a6a8a' : pick(['#5a88b0', '#8ab8dc', '#bfe0f8'], 0.35 + (VIA - 1 - y) / 12, x, y));
  for (let x = STN.x0; x <= STN.x1; x++) {
    const ry = stnRoof(x), u = Math.abs(x - STN_MID) / STN_HW;
    p.set(x, ry, u < 0.6 ? '#fff0c8' : '#f0d8a0'); p.set(x, ry + 1, (x - STN.x0) % 4 === 0 ? '#a8844e' : '#d0ac70'); p.set(x, ry + 2, '#8a6a42');
    p.set(x, ry + 3, '#5a4630');
    if (u > 0.82) for (let y = ry + 3; y < VIA - 1; y++) p.set(x, y, x < STN_MID ? '#c8a468' : '#8a6a42');   // the shell's ends come down to the deck
  }
  return trim(p);
}
/** driverless metro train: white cars, a dark glazed band with lit windows, rounded noses */
const TRAIN_ROWS = (() => {
  const w = 66, rows = [];
  for (let y = 0; y < 6; y++) {
    let s = '';
    for (let i = 0; i < w; i++) {
      const inset = y === 0 || y === 5 ? 2 : y === 1 ? 1 : 0, gap = i > 0 && i < w - 1 && i % 13 === 0;
      if (i < inset || i >= w - inset) { s += '.'; continue; }
      if (i >= w - 4 && y >= 1 && y <= 3 && i - (w - 4) >= y - 1) { s += 'k'; continue; }     // sloped windscreen
      if (y === 0) s += 'r'; else if (y === 5) s += 'u';
      else if (gap) s += 'j';
      else if (y === 2 || y === 3) s += (i % 13) % 4 === 3 ? 'd' : y === 2 ? 'L' : 'l';
      else s += y === 1 ? 'B' : 'b';
    }
    rows.push(s);
  }
  rows[4] = rows[4].slice(0, -2) + 'HH';
  rows[4] = 'T' + rows[4].slice(1);
  return rows;
})();
const TRAIN_KEY = { r: '#aab0c2', B: '#eef2f8', b: '#d6dae6', L: '#d8f0ff', l: '#a8d0f0', d: '#1c2234', j: '#3a3e4e', u: '#2a2e3a',
  k: '#0e1422', H: '#fffbe8', T: '#ff3a3a' };
// three calls a loop: in from behind the souk, a stop at the station, out behind the café (or the other way)
const TRAINS = [{ a: 14, dir: 1 }, { a: 92, dir: -1 }, { a: 202, dir: 1 }];
const TR_W = 66, TR_IN = 9, TR_DWELL = 6, TR_OUT = 8, TR_STOP = Math.round(STN_MID - TR_W / 2);
const TR_L = SOUK.x1 - TR_W - 2, TR_R = CAFE.x + 2;   // fully hidden behind the souk / the café wall
function trainX(t) {
  for (const tr of TRAINS) {
    const s = t - tr.a;
    if (s < 0 || s >= TR_IN + TR_DWELL + TR_OUT) continue;
    const from = tr.dir > 0 ? TR_L : TR_R, to = tr.dir > 0 ? TR_R : TR_L;
    let x;
    if (s < TR_IN) { const u = s / TR_IN; x = lerp(from, TR_STOP, 1 - (1 - u) * (1 - u)); }
    else if (s < TR_IN + TR_DWELL) x = TR_STOP;
    else { const u = (s - TR_IN - TR_DWELL) / TR_OUT; x = lerp(TR_STOP, to, u * u); }
    return { x: Math.round(x), dir: tr.dir, stopped: s >= TR_IN - 0.3 && s < TR_IN + TR_DWELL };
  }
  return null;
}

/* ================= The souk on the lakeside: sandstone, a lit arcade of pointed arches, wind towers, a dome ================= */
const STONE = ['#2e2224', '#46302a', '#624432', '#7e5a3c', '#9a7048', '#b68a58', '#d0a468', '#e6bc7c'];
const TOWERS_W = [{ x: 42, top: 126 }, { x: 96, top: 130 }, { x: 146, top: 123 }];   // barjeel
const DOME = { x: 121, r: 7 };
const ARCH_X = [];
for (let x = SOUK.x0 + 4; x + 8 <= SOUK.x1 - 3; x += 12) ARCH_X.push(x);
const PROM_LAMPS = [];
function renderNear() {
  const p = pix(W, H), r = rng(29);
  const { x0, x1, roof } = SOUK;
  for (let x = x0; x <= x1; x++) for (let y = roof; y < WL; y++) {
    let v = 0.3 + 0.42 * smooth(roof, 176, y) + (vnoise(x / 4, y / 3, 12) - 0.5) * 0.14 - (x > x1 - 3 ? 0.25 : 0);
    if (x === x0) v -= 0.2;
    p.set(x, y, pick(STONE, v, x, y));
  }
  for (let x = x0; x <= x1; x++) {                    // crenellated parapet and cornice
    const m = (x - x0) % 5;
    if (m < 3) p.set(x, roof - 1, m === 1 ? '#e6bc7c' : '#b68a58');
    if (m === 1) p.set(x, roof - 2, '#d0a468');
    p.set(x, roof, '#e6bc7c'); p.set(x, roof + 2, '#d8ac6c'); p.set(x, roof + 3, '#4a3428');
  }
  for (let x = x0 + 8; x < x1 - 6; x += 12) {         // upper floor: small arched windows, some with a lit lattice
    const lit = r() < 0.55;
    for (let y = roof + 5; y < roof + 12; y++) for (let i = 0; i < 4; i++) {
      if (y === roof + 5 && (i === 0 || i === 3)) continue;
      p.set(x + i, y, lit ? ((i + y) % 2 ? '#e8a050' : '#7a4424') : '#241816');
    }
  }
  for (let x = x0; x <= x1; x++) { p.set(x, roof + 13, '#e0b474'); p.set(x, roof + 14, '#6a4a34'); }   // string course
  // the arcade: pointed arches, warm shops inside
  for (const ax of ARCH_X) {
    const shop = r();
    for (let y = 162; y < 176; y++) {
      const half = y === 162 ? 1 : y === 163 ? 2 : y === 164 ? 3 : 4;
      for (let i = 4 - half; i < 4 + half; i++) {
        let col = pick(['#b86a30', '#e8984a', '#ffc070', '#ffe2a8'], 1 - (y - 162) / 16 + (shop - 0.5) * 0.3, ax + i, y);
        if (y === 170 && shop > 0.4) col = '#8a4a26';                       // a shelf of goods
        if (y > 170 && (i === 2 || i === 5) && hash(ax, i, 3) < 0.35) col = '#3a2018';   // a shopper
        if (y === 171 && (i === 2 || i === 5) && hash(ax, i, 3) < 0.35) col = '#2a1814';
        p.set(ax + i, y, col);
      }
    }
    p.set(ax + 3, 165, '#fff4d0'); p.set(ax + 4, 165, '#fff4d0');           // a hanging lamp
    for (let y = 162; y < 176; y++) { p.set(ax - 1, y, '#f0c888'); p.set(ax + 8, y, '#8a6444'); }   // lit pier faces
  }
  for (let x = x0; x <= x1; x++) { p.set(x, 176, '#b08a60'); p.set(x, 177, '#4a3428'); }
  // wind towers: tall square towers with louvred openings at the top, poles poking out, uplit from the roof
  for (const { x, top } of TOWERS_W) {
    for (let y = top; y < roof; y++) for (let i = 0; i < 9; i++) {
      let v = 0.35 + 0.35 * smooth(top, roof, y) - (i > 6 ? 0.22 : 0) + (i === 0 ? 0.15 : 0);
      let col = pick(STONE, v, x + i, y);
      if (y > top + 3 && y < top + 13 && (i === 2 || i === 4 || i === 6)) col = y === top + 4 ? '#5a3a24' : '#1e1412';
      if (y === top + 2 || y === top + 13) col = '#e6bc7c';
      p.set(x + i, y, col);
    }
    for (let i = 0; i < 9; i += 2) p.set(x + i, top - 1, '#d0a468');
    p.set(x - 1, top + 6, '#4a3020'); p.set(x + 9, top + 6, '#3a2418'); p.set(x - 1, top + 10, '#4a3020'); p.set(x + 9, top + 10, '#3a2418');
  }
  // a small dome on a drum
  for (let y = roof - 4; y < roof; y++) for (let dx = -5; dx <= 5; dx++) p.set(DOME.x + dx, y, dx < -3 ? '#e0b474' : dx > 3 ? '#6a4a34' : '#a87e50');
  for (let dy = -DOME.r; dy <= 0; dy++) for (let dx = -DOME.r; dx <= DOME.r; dx++) {
    if (dx * dx + dy * dy * 1.3 > DOME.r * DOME.r) continue;
    p.set(DOME.x + dx, roof - 5 + dy, pick(STONE, 0.75 - (dx + DOME.r) / 14 * 0.55 - dy * -0.01, DOME.x + dx, dy));
  }
  for (let y = roof - 16; y < roof - 12; y++) p.set(DOME.x, y, '#f0d090');

  // the far promenade: mall fronts, a stepped podium under the Burj, balustrade, lamps and little uplit palms
  for (let x = SOUK.x1 + 1; x < CAFE.x; x++) {
    const seg = Math.floor(x / 23), top = x > BK.cx - 20 && x < BK.cx + 20 ? 169 : 163 + Math.floor(hash(seg, 3, 5) * 6);
    for (let y = top; y < 175; y++) {
      let col = y === top ? '#3a3458' : y === top + 1 ? '#5a5074' : '#18162c';
      if (y >= top + 2 && y <= top + 5 && x % 23 > 1) col = (x % 5 === 0) ? '#2a2842' : hash(seg, y, 7) < 0.5 ? pick(['#8ab0d8', '#cfe6ff'], (y - top) / 5, x, y) : pick(['#c88a50', '#ffd8a0'], (y - top) / 5, x, y);
      p.set(x, y, col);
    }
    p.set(x, 175, '#9a8a98'); p.set(x, 176, '#6a5a70'); p.set(x, 177, '#2a2238');
  }
  for (let x = SOUK.x1 + 8; x < CAFE.x; x += 18) { for (let y = 168; y < 175; y++) p.set(x, y, '#2a2438'); p.set(x, 167, '#ffe0a0'); PROM_LAMPS.push([x, 167]); }
  for (let x = SOUK.x1 + 17; x < CAFE.x - 4; x += 26) {
    for (let y = 165; y < 175; y++) p.set(x, y, y > 171 ? '#c89a60' : '#6a4a30');
    for (const [dx, dy] of [[-3, 1], [-2, 0], [-1, -1], [0, -1], [1, -1], [2, 0], [3, 1], [-2, 1], [2, 1], [-4, 2], [4, 2]]) p.set(x + dx, 165 + dy, dy > 0 ? '#5a6a2e' : '#1a2a1c');
  }
  for (let x = SOUK.x0 + 10; x < SOUK.x1; x += 24) { for (let y = 170; y < 176; y++) p.set(x, y, '#3a2418'); p.set(x, 169, '#ffe8b0'); PROM_LAMPS.push([x, 169]); }
  return trim(p);
}
const WALKERS = [];
{ const r = rng(333); for (let i = 0; i < 9; i++) WALKERS.push({ k: 1 + Math.floor(r() * 2), ph: r(), dir: r() < 0.5 ? 1 : -1, col: r() < 0.45 ? '#e8e0d0' : r() < 0.5 ? '#141018' : '#6a4a5a' }); }

/* ================= The rooftop LED hoarding (the city's one billboard) =================
   A steel-cased LED screen on a catwalk trestle on the roof of a mid-rise just right of the Burj, with red aircraft
   lamps on its top corners and a chasing LED border. The face (BB) shows the city's own ad for an invented karak tea
   (a steaming glass, the word KARAK and a line that changes every 5 s) or, in promo captures, the pitch. */
const BB_POSTS = [BBX.l + 2, BBX.l + 18, BBX.r - 18, BBX.r - 2];
const BB_BEACONS = [[BBX.l + 1, BBX.t - 4], [BBX.r - 1, BBX.t - 4]];
const STEEL = { d: '#0c0a16', m: '#221e34', h: '#3a3450', l: '#5a5474' };
function renderBillboardFrame() {
  const p = pix(W, H), { l, r: R, t: T, b: B } = BBX, T0 = B + 1, T1 = BB_BLD.top - 1;
  for (let k = 0; k + 1 < BB_POSTS.length; k++) { const a = BB_POSTS[k] + 1, b = BB_POSTS[k + 1] - 1; lineP(p, a, T0 + 2, b, T1, '#2e2a44'); lineP(p, a, T1, b, T0 + 2, '#2e2a44'); }
  for (const x of BB_POSTS) for (let y = T0; y <= T1; y++) { p.set(x, y, STEEL.l); p.set(x + 1, y, STEEL.d); }
  for (let x = l - 2; x <= R + 2; x++) { p.set(x, T0 + 1, (x - l) % 4 === 1 ? STEEL.l : STEEL.h); p.set(x, T0 + 2, x & 1 ? '#3a3450' : '#302a46'); }
  for (const [x, y] of BB_BEACONS) { for (let yy = y + 1; yy < T; yy++) p.set(x, yy, STEEL.h); }
  for (let x = l; x <= R; x++) { p.set(x, T, x < l + 5 ? STEEL.l : STEEL.h); p.set(x, B, STEEL.d); }
  for (let y = T + 1; y < B; y++) { p.set(l, y, STEEL.h); p.set(R, y, STEEL.m); }
  return trim(p);
}
// the ad: a burgundy LED screen, a glass of karak on a saucer (steam per frame), KARAK in big gold letters, a small line
const GLASS_ROWS = ['.WWWWWWWW.', '.gFFFFFFg.', '.gTtTTTTg.', '.gTtTTTTg.', '..gtTTTg..', '..gtTTTg..', '..gTTTTg..', '...gggg...', 'SSSSSSSSSS', '.ssssssss.'];
const GLASS_KEY = { W: '#f4f8ff', g: '#9ab0c8', F: '#f4dcae', T: '#b8662a', t: '#e8a45c', S: '#e0d6cc', s: '#8a7a78' };
const GLASS = { x: 1, y: 8 };
const BB_LINES = ['TEA 24/7', '1 DIRHAM', 'HOT&SWEET'];
const BB_TX = 14;
function text2(c, s, x, y, col) {                    // FONT3 at double size, 1 px between letters (7 px advance)
  const [cv, cx] = makeCanvas(3, 5);
  c.imageSmoothingEnabled = false;
  for (const ch of s) { cx.clearRect(0, 0, 3, 5); text(cx, ch, 0, 0, col); c.drawImage(cv, 0, 0, 3, 5, x, y, 6, 10); x += 7; }
}
function renderBillboardAd(bright) {
  const { w, h } = BB;
  const c = ditherFill(w, h, ['#240612', '#3a0c1a', '#541424', '#6e1c2c'], (i, j) =>
    0.55 + 0.35 * Math.exp(-(((j - 1) / 4) ** 2)) - 0.3 * Math.max(0, 1 - Math.min(i, w - 1 - i) / 3) - 0.2 * (j / h));
  const x = c.getContext('2d');
  GLASS_ROWS.forEach((row, j) => { for (let i = 0; i < row.length; i++) { const col = GLASS_KEY[row[i]]; if (col) rect(x, GLASS.x + i, GLASS.y + j, 1, 1, col); } });
  text2(x, 'KARAK', BB_TX + 1, 3, '#2a040c');
  text2(x, 'KARAK', BB_TX, 2, bright ? '#fff6d0' : '#ffc43a');
  return c;
}
function renderBillboardLine(s) {
  const [c, x] = makeCanvas(BB.w - BB_TX, 6);
  text(x, s, 0, 0, '#ffe6bc');
  return c;
}
/** the station's front half, drawn over the train: the shell's closed ends and its glass wall (mullions, a cool tint) */
function renderStationFront() {
  const p = pix(W, H);
  for (let x = STN.x0; x <= STN.x1; x++) {
    const ry = stnRoof(x), u = Math.abs(x - STN_MID) / STN_HW;
    if (u > 0.82) { for (let y = ry + 3; y < VIA - 1; y++) p.set(x, y, x < STN_MID ? '#b89458' : '#7a5c3a'); continue; }
    for (let y = VIA - 9; y < VIA - 1; y++) p.set(x, y, (x - STN.x0) % 5 === 0 ? '#6a8aaa' : '#bfe0ff', (x - STN.x0) % 5 === 0 ? 150 : 38);
  }
  return trim(p);
}

/* ================= The lake: dark violet water, mirrored city, streaks; the calm returns between shows ================= */
function renderWater() {
  const hh = EDGE - WL, P = ['#221a3a', '#1a1430', '#141028', '#0e0b20', '#0a0818'];
  const p = pix(W, hh);
  for (let y = 0; y < hh; y++) for (let i = 0; i < W; i++) p.set(i, y, pick(P, Math.pow(y / hh, 0.7) * 0.999, i, y));
  return p.done();
}
const STREAKS = [
  { x: BK.cx - 2, w: 9, col: '#c8d4f4', a: 0.36, seed: 1 }, { x: BK.cx - 1, w: 3, col: '#ffffff', a: 0.42, seed: 8 },
  { x: 185, w: 6, col: '#ffd890', a: 0.3, seed: 2 }, { x: 374, w: 4, col: '#cfe6ff', a: 0.26, seed: 3 },
  { x: 356, w: 6, col: '#bfe0ff', a: 0.22, seed: 4 }, { x: 364, w: 14, col: '#ffe6b0', a: 0.18, seed: 5 },
  ...ARCH_X.filter((_, i) => i % 2 === 0).map((x, i) => ({ x: x + 4, w: 7, col: '#ffb860', a: 0.3, seed: 10 + i })),
];

/* ================= The fountain: a line of nozzles across the lake, choreographed twice a loop =================
   Kinds of move: 'rise' (the row wakes from the centre out), 'wave' (a ripple travels along the row), 'chase' (a
   pulse runs back and forth), 'sway' (the arcing jets swing together), 'cross' (they swing against each other),
   'shoot' (tall shooters fire one after another), 'finale' (everything at once, then the mist). */
const FTOP = 96;                                     // highest row a jet reaches
const RIP = [], ARCS = [], SHOOT = [104, 146, 192, BK.cx, 288, 334, 376];
for (let x = 82; x <= 398; x += 4) RIP.push(x);
// each nozzle's own strength and brightness: a slow drift along the row plus a little of its own, so no two neighbours match
const RIP_J = RIP.map((x, i) => 0.84 + 0.1 * Math.sin(x * 0.031 + 1.3) + 0.12 * hash(i, 3, 17));
const RIP_B = RIP.map((x, i) => 0.72 + 0.14 * Math.sin(x * 0.047) + 0.22 * hash(i, 5, 17));
for (let k = 0; k < 12; k++) ARCS.push(96 + k * 28);
const SHOWS = [
  { a: 36, b: 72, secs: [['rise', 0, 5], ['wave', 5, 12], ['sway', 12, 19], ['shoot', 19, 26.5], ['cross', 26, 32], ['finale', 31.5, 36]] },
  { a: 162, b: 198, secs: [['rise', 0, 5], ['sway', 5, 11], ['chase', 11, 18], ['shoot', 18, 25.5], ['wave', 25, 31], ['finale', 30.5, 36]] },
];
const SHOT_ORDER = [0, 6, 1, 5, 2, 4, 3];
const SHOT_GAP = 0.95, SHOT_UP = 0.8, SHOT_HOLD = 0.6, SHOT_FALL = 1.5;
const showLevel = t => Math.max(...SHOWS.map(s => env(t, s.a, s.b + 4, 1.5)));
const MISTS = [[42, 114], [168, 236]];               // spray hanging over the lake, drifting off after each show
const mistLevel = t => Math.max(...MISTS.map(([a, b]) => env(t, a, b, 14)));
let FC, fx, SHOOT_C, JG_C, JET_C, RH;                // offscreen jet layer, the shooters' column, a jet's glow, the row jets, their heights this frame
/** a water column sprite: bright core, gold-lit lower part (underwater lamps), the top breaking up into spray */
function renderColumn(w, h, core, mid, edge, gold, dissolve) {
  const p = pix(w, h), m = (w - 1) / 2;
  for (let y = 0; y < h; y++) {
    const up = 1 - y / (h - 1), top = up > 1 - dissolve ? (up - (1 - dissolve)) / dissolve : 0;
    for (let i = 0; i < w; i++) {
      const d = Math.abs(i - m) / (m + 0.5);
      if (top > 0 && top * (1 + d) > bayer(i, y) + 0.1) continue;
      let col = d < 0.3 ? core : d < 0.65 ? mid : edge;
      if (up < 0.22 && d >= 0.3) col = gold;
      p.set(i, y, col, Math.round((d < 0.3 ? 1 : d < 0.65 ? 0.7 : 0.3) * 255));
    }
  }
  return p.done();
}
function renderJetGlow() {                           // soft vertical glow, stretched over a jet (drawn additively)
  const w = 15, h = 48, p = pix(w, h), rgb = hex('#ffe2b0');
  for (let y = 0; y < h; y++) for (let i = 0; i < w; i++) {
    const v = Math.pow(Math.max(0, 1 - Math.abs(i - 7) / 7.5), 1.6) * Math.pow(Math.sin(Math.PI * (y + 0.5) / h), 0.5);
    const q = Math.floor(v * 5 + bayer(i, y)) / 5;
    if (q > 0) p.set(i, y, rgb, Math.round(q * 0.5 * 255));
  }
  return p.done();
}
/* the row jets, one sprite per height so nothing is stretched: a thin stem of water, bright where the lamps under the
   water light it and thinning as it climbs, opening into a plume of spray that blooms into a dithered crown of mist and
   falls back as drops on both sides. Neighbouring crowns merge into one soft crest. JET_V variants of the spray, swapped
   a few times a second, make it shimmer. */
const JET_N = 60, JET_V = 4, JET_W = 15, JET_CAP = 9, JET_CH = JET_N + JET_CAP;
function renderJets() {
  const p = pix(JET_W * JET_N, JET_CH * JET_V), m = JET_W >> 1;
  const CORE = hex('#fff8ec'), SKIN = hex('#ffe6bc'), GOLD = hex('#ffc860'), MIST = hex('#fff2dc'), DROP = hex('#ffe8c4');
  for (let v = 0; v < JET_V; v++) for (let h = 1; h <= JET_N; h++) {
    const ox = (h - 1) * JET_W + m, base = v * JET_CH + JET_CH - 1, top = base - h + 1;
    const on = (i, y, dens) => dens > 0.55 * bayer(ox + i, y) + 0.45 * hash(ox + i, y, 31 + v);
    const spread = 1.4 + h * 0.07;                   // half-width of the plume at the top
    for (let j = 0; j < h; j++) {                     // the stem and the plume, j rows above the nozzle
      const u = h > 1 ? j / (h - 1) : 1, y = base - j, brk = smooth(0.45, 1, u), hw = 1 + brk * (spread - 1);
      if (on(0, y, 1.05 - brk * 0.7)) p.set(ox, y, CORE, Math.round(255 * (0.95 - u * 0.4 - brk * 0.15)));
      for (const s of [-1, 1]) {
        if (on(s, y, 0.55 + brk * 0.25)) p.set(ox + s, y, u < 0.18 ? GOLD : SKIN, Math.round(255 * (0.34 + brk * 0.2)));
        for (let i = 2; i <= Math.ceil(hw); i++) {
          const dens = brk * 0.55 * (1 - (i - 1) / (hw + 0.5));
          if (on(s * i, y, dens)) p.set(ox + s * i, y, MIST, 130);
        }
        const fall = Math.round(hw + 1 + (1 - u) * h * 0.05);                         // drops falling back, drifting out as they fall
        if (h > 10 && u > 0.3 && u < 0.9 && on(s * fall, y, 0.14 * smooth(0.3, 0.55, u))) p.set(ox + s * fall, y, DROP, 120);
      }
    }
    for (let j = 0; j < Math.min(3, h); j++) for (let i = -3; i <= 3; i++) {   // froth where it leaves the water
      const dens = (1 - j / 3) * (1 - Math.abs(i) / 4) * Math.min(1, 0.3 + h / 20);
      if (i && on(i, base - j, dens)) p.set(ox + i, base - j, MIST, 170);
    }
    const cap = Math.min(JET_CAP - 1, 1 + Math.round(h * 0.13));
    for (let k = 1; k <= cap; k++) {                  // the bloom: a soft crown of mist over the top
      const y = top - k, hw = Math.round(spread * (1 - 0.55 * (k / (cap + 1)) ** 2));
      for (let i = -hw; i <= hw; i++) {
        const dens = (1 - k / (cap + 1)) * (1 - Math.abs(i) / (hw + 1.2)) * 1.2;
        if (on(i, y, dens)) p.set(ox + i, y, MIST, Math.round(255 * clamp(0.3 + 0.5 * dens, 0, 0.85)));
      }
    }
  }
  return p.done();
}
const FB = FY - FTOP;                                // the nozzle line in the offscreen layer's rows
const jetVar = (i, gs) => Math.floor(hash(i, gs, 23) * JET_V);
function jet(x, h, v, a) {                           // a row jet reaching h px above the water
  h = Math.min(JET_N, Math.round(h));
  if (h < 1 || a <= 0.02) return;
  fx.globalAlpha = Math.min(1, a);
  fx.drawImage(JET_C, (h - 1) * JET_W, v * JET_CH, JET_W, JET_CH, Math.round(x) - (JET_W >> 1), FB - JET_CH, JET_W, JET_CH);
  fx.globalAlpha = 1;
}
function jetColumn(x, h0, h1, spr, a) {             // a column from h0 to h1 px above the water
  if (h1 - h0 < 1 || a <= 0.02) return;
  fx.globalAlpha = Math.min(1, a);
  fx.drawImage(spr, Math.round(x - (spr.width >> 1)), Math.round(FB - h1), spr.width, Math.round(h1 - h0));
  fx.globalAlpha = 1;
}
function spray(x, y, r, n, seed, a, gs) {            // shimmering droplets around a point
  fx.fillStyle = '#fff4dc';
  for (let k = 0; k < n; k++) {
    const ang = hash(seed, k, gs) * TAU, d = Math.sqrt(hash(seed + 7, k, gs));
    fx.globalAlpha = a * (1 - d * 0.7);
    fx.fillRect(Math.round(x + Math.cos(ang) * d * r * 1.4), Math.round(y + Math.sin(ang) * d * r * 0.8), 1, 1);
  }
  fx.globalAlpha = 1;
}
function plume(x, top, len, spread, n, seed, a, gs) { // a feathery crown of spray over a shooter, widest at the top
  fx.fillStyle = '#fff6e4';
  for (let k = 0; k < n; k++) {
    const f = Math.pow(hash(seed, k, gs), 0.8), w = spread * (1 - f * 0.75);
    fx.globalAlpha = a * (0.45 + 0.55 * (1 - f));
    fx.fillRect(Math.round(x + (hash(seed + 5, k, gs) * 2 - 1) * w), Math.round(top + f * len), 1, 1);
  }
  fx.globalAlpha = 1;
}
function cascade(x, top, spread, n, seed, a, gs) {   // droplets raining back down around a column
  fx.fillStyle = '#ffe8c4';
  for (let k = 0; k < n; k++) {
    const f = hash(seed, k, gs), side = hash(seed + 1, k, gs) < 0.5 ? -1 : 1;
    fx.globalAlpha = a * (1 - f * 0.6);
    fx.fillRect(Math.round(x + side * spread * (0.45 + 0.55 * Math.sqrt(f))), Math.round(top + f * (FB - top)), 1, 1);
  }
  fx.globalAlpha = 1;
}
function jetArc(x, dx, h, a, seed, gs, tops) {       // a parabola from the nozzle, apex h px up and dx px across
  if (h < 2 || a <= 0.02) return;
  const n = Math.round((h * 2 + Math.abs(dx)) * 1.2);
  for (let k = 0; k <= n; k++) {
    const s = k / n * 1.95, X = Math.round(x + dx * s), Y = Math.round(FB - h * (2 * s - s * s));
    if (s > 1.05 && hash(seed, k, gs) < (s - 1.05) * 0.8) continue;              // the falling half breaks up
    const fa = a * (s < 1 ? 1 : 1.3 - s * 0.4);
    fx.globalAlpha = fa; fx.fillStyle = '#fffaf0'; fx.fillRect(X, Y, 1, 1);
    fx.globalAlpha = fa * 0.5; fx.fillStyle = s < 0.45 ? '#ffd48a' : '#ffeccc'; fx.fillRect(X, Y + 1, 1, 1);
  }
  fx.globalAlpha = 1;
  spray(x + dx * 1.95, FB - 1, 2, 4, seed + 9, a * 0.7, gs);                        // splash where it lands
  spray(x + dx, FB - h, 2, 3, seed + 3, a * 0.6, gs);
  if (tops && h > 20) tops.push([x + dx, FY - h, a * 0.45, 0]);
}
/** one shooter burst at s seconds after firing: a wide column racing up, a plume, then collapsing into falling spray */
function shot(x, s, hmax, seed, gs, tops) {
  if (s < 0 || s > SHOT_UP + SHOT_HOLD + SHOT_FALL) return;
  let h0 = 0, h1, a = 1, fall = 0;
  if (s < SHOT_UP) h1 = hmax * (1 - (1 - s / SHOT_UP) ** 2);
  else if (s < SHOT_UP + SHOT_HOLD) h1 = hmax + (s - SHOT_UP) * 4;
  else { fall = (s - SHOT_UP - SHOT_HOLD) / SHOT_FALL; h1 = (hmax + SHOT_HOLD * 4) * (1 - fall * fall * 0.85); h0 = hmax * 0.85 * fall; a = 1 - fall * 0.7; }
  const top = FB - h1, grow = Math.min(1, s / SHOT_UP);
  jetColumn(x, h0, h1, SHOOT_C, a);
  plume(x, top - 2, 6 + grow * 10, 2 + grow * 4 + fall * 6, 34, seed, 0.95 * a, gs);
  if (s > SHOT_UP * 0.7) cascade(x, top + 4, 4 + grow * 3 + fall * 8, 26, seed + 40, 0.7 * a * Math.min(1, (s - SHOT_UP * 0.7) / 0.5), gs);
  tops.push([x, FY - h1, a, h1 - h0]);
}
const pingpong = v => { const f = frac(v / 2) * 2; return f < 1 ? f : 2 - f; };
const archAt = x => 0.5 + 0.5 * Math.cos(Math.PI * clamp((x - BK.cx) / 170, -1, 1));   // the row stands taller towards its middle
function drawFountain(t) {
  const S = SHOWS.find(s => t >= s.a - 0.1 && t < s.b + 3);
  if (!S) return null;
  const u = t - S.a, gs = step(t, 2400), tops = [];
  fx.setTransform(1, 0, 0, 1, 0, 0); fx.globalAlpha = 1; fx.globalCompositeOperation = 'source-over'; fx.clearRect(0, 0, W, FB + 1);
  RH.fill(0);
  const ripple = (i, h, a) => { h *= RIP_J[i]; RH[i] = Math.max(RH[i], h * a); jet(RIP[i], h, jetVar(i, gs), a * RIP_B[i]); };
  for (const [kind, s0, s1] of S.secs) {
    if (u < s0 - 0.1 || u > s1 + 3.5) continue;
    const e = env(u, s0, s1, 0.7), v = u - s0;
    if (kind === 'rise' || kind === 'wave' || kind === 'chase') {
      // the chase leaves a wake: jets it has passed take a moment to fall back
      const xp = [0, 0.35, 0.7].map(d => 80 + 320 * pingpong((v - d) / 3.4)), bump = d => smooth(0, 1, 1 - Math.abs(d) / 32);
      RIP.forEach((x, i) => {
        let h;
        if (kind === 'rise') { const dl = Math.abs(x - BK.cx) / 160 * 2.2; h = (15 + 11 * archAt(x) + 2.5 * Math.sin(v * 2.2 - x * 0.06) + 3 * Math.sin(x * 0.15 + v * 1.3)) * smooth(dl, dl + 1.2, v); }
        else if (kind === 'wave') h = (9 + 25 * (0.5 + 0.5 * Math.sin(x * 0.042 - v * 3.4))) * (0.86 + 0.14 * Math.sin(x * 0.012 + v * 0.8));
        else h = 5 + 36 * Math.max(bump(x - xp[0]), 0.72 * bump(x - xp[1]), 0.45 * bump(x - xp[2]));
        ripple(i, h * e, 0.95);
      });
      if (kind === 'wave' && e > 0.3) ARCS.forEach((x, k) => jetArc(x, (k % 2 ? 7 : -7), 12 * e, 0.7, 50 + k, gs));
    } else if (kind === 'sway' || kind === 'cross') {
      const sw = Math.sin(v * 1.8);
      ARCS.forEach((x, k) => {
        const dx = kind === 'sway' ? 24 * sw + (k - 5.5) * 1.2 : (k % 2 ? 1 : -1) * 22 * sw;
        jetArc(x, dx, (36 + 4 * Math.sin(k * 1.7)) * e, 0.95, 60 + k, gs, tops);
      });
      RIP.forEach((x, i) => { if (i % 4 === 0) ripple(i, (kind === 'cross' ? 9 : 5) * e, 0.75); });
    } else if (kind === 'shoot') {
      SHOT_ORDER.forEach((k, i) => shot(SHOOT[k], v - i * SHOT_GAP, k === 3 ? 88 : 60 + (k % 3) * 7, 80 + k, gs, tops));
      RIP.forEach((x, i) => ripple(i, 6 * e * (0.65 + 0.35 * Math.sin(x * 0.08 - v * 4)), 0.75));
    } else if (kind === 'finale') {
      SHOOT.forEach((x, k) => shot(x, v - 0.2 - Math.abs(k - 3) * 0.1, k === 3 ? 96 : 76 - Math.abs(k - 3) * 3, 90 + k, gs, tops));
      const f = e * smooth(0, 0.6, v);
      RIP.forEach((x, i) => ripple(i, (22 + 12 * archAt(x) + 4 * Math.sin(x * 0.11 - v * 3)) * f, 0.95));
      ARCS.forEach((x, k) => jetArc(x, (k - 5.5) * 5, 46 * f, 0.9, 120 + k, gs));
    }
  }
  return tops;
}
/** composite the jet layer: the jets, their glow, a rippled mirror under the nozzle line, foam and underwater lights */
function drawFountainLayer(t, tops) {
  const lv = showLevel(t);
  for (let x = RIP[0]; x <= RIP[RIP.length - 1]; x += 20) halo(HL.nozzle, x, FY + 1, lv * (0.6 + 0.2 * wave(t, 300, x / 97)));
  halo(HL.fount, BK.cx, FY - 16, lv * 0.9);
  if (!tops) return;
  ctx.globalCompositeOperation = 'lighter';         // a faint veil of lit spray behind each stretch of the row
  for (let g = 0; g < RIP.length; g += 6) {
    let hs = 0;
    for (let i = g; i < g + 6; i++) hs += RH[i] || 0;
    const h = hs / 6;
    if (h < 4) continue;
    ctx.globalAlpha = 0.4 * Math.min(1, h / 16);
    ctx.drawImage(JG_C, RIP[g] - 6, Math.round(FY - h - 5), 32, Math.round(h) + 7);
  }
  ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
  ctx.drawImage(FC, 0, FTOP);
  const rows = EDGE - FY - 2;
  for (let k = 0; k < rows; k++) {
    const sy = FB - 1 - Math.floor(k * 1.7);
    if (sy < 0) break;
    const off = Math.round(Math.sin(TAU * 60 * t / LOOP + k * 0.8) * (0.6 + k * 0.08));
    ctx.globalAlpha = 0.36 * (1 - k / rows);
    ctx.drawImage(FC, 0, sy, W, 1, off, FY + 1 + k, W, 1);
  }
  ctx.globalAlpha = 1;
  ctx.globalCompositeOperation = 'lighter';
  for (const [x, y, a, h] of tops) if (h > 0) { ctx.globalAlpha = 0.5 * a; ctx.drawImage(JG_C, x - 7, Math.round(y) - 6, 15, Math.round(h) + 8); }
  ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
  for (const [x, y, a] of tops) halo(HL.jetTop, x, y, a * 0.9);
  const gs = step(t, 2400);
  ctx.fillStyle = '#fff2dc';
  for (let k = 0; k < 60; k++) {                     // spray landing along the nozzle line
    if (hash(k, gs, 71) > lv * 0.8) continue;
    ctx.globalAlpha = 0.6; ctx.fillRect(RIP[0] + Math.floor(hash(k, gs, 72) * 322), FY - 1 + Math.floor(hash(k, gs, 73) * 5), 1, 1);
  }
  ctx.globalAlpha = 1;
}
/* mist: a pale gold veil that grows over the jets and drifts off down the lake */
function makeMist() {
  const h = 70, [c, cx] = makeCanvas(W, h), img = cx.createImageData(W, h), d = img.data, A = hex('#d8c8c0'), B = hex('#f0dcc0');
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    const v = y / (h - 1), prof = Math.pow(Math.sin(Math.PI * Math.pow(v, 1.3)), 1.4);
    let nz = 0.45 + 0.3 * Math.sin(TAU * 3 * x / W + y * 0.09) + 0.22 * Math.sin(TAU * 7 * x / W - y * 0.23 + 1.3) + 0.12 * Math.sin(TAU * 17 * x / W + y * 0.5);
    nz = Math.max(0, nz) ** 1.6 * 1.3;
    const q = Math.floor(clamp(prof * nz, 0, 1) * 5 + bayer(x, y)) / 5;
    if (q <= 0) continue;
    const o = (y * W + x) * 4;
    d[o] = A[0] + (B[0] - A[0]) * v; d[o + 1] = A[1] + (B[1] - A[1]) * v; d[o + 2] = A[2] + (B[2] - A[2]) * v; d[o + 3] = Math.round(q * 255);
  }
  cx.putImageData(img, 0, 0);
  return c;
}
function drawMist(t) {
  const m = mistLevel(t);
  if (m <= 0.01) return;
  ctx.globalAlpha = m * 0.34;
  drawScroll(MIST_C, FY - 62, 3, t);
  ctx.globalAlpha = 1;
}

/* ================= The abra: a wooden water taxi with a canopy, lanterns and a few passengers ================= */
const ABRA_ROWS = [
  '.......cccccccccccccccccccc.......',
  '......CCCCCCCCCCCCCCCCCCCCCC......',
  '......f.f.f.f.f.f.f.f.f.f.f.......',
  '......p..o....i.....i...o..p......',
  '..m...p.iw..i.w.i..iw..i...p......',
  '..M...p.ww..w.w.k..ww..k...p....GG',
  '..M...p.ww..wwk.k..wwk.k...p...Gg.',
  'gGGGGGGGGGGGGGGGGGGGGGGGGGGGGGGGg.',
  '.dHHHHHHHHHHHHHHHHHHHHHHHHHHHHHd..',
  '..dxxxxxxxxxxxxxxxxxxxxxxxxxxxd...',
  '....kkkkkkkkkkkkkkkkkkkkkkkkk.....',
];
const ABRA_KEY = { c: '#f0e2c0', C: '#b8966a', f: '#8a6440', p: '#5a3a24', o: '#ffd070', i: '#2a1c18', w: '#e4dccc', k: '#241818',
  m: '#2a1c18', M: '#d8d0c0', G: '#c88c52', g: '#eab070', H: '#8a5630', d: '#5a3820', x: '#5e3a22' };
const ABRA_LANTERNS = [9, 24];                        // x of the two lanterns in the sprite (row 3)
const ABRAS = [{ a: 4, b: 116, x0: -40, x1: 500, wl: 216 }, { a: 124, b: 236, x0: 500, x1: -40, wl: 221 }];
function boatReflect(spr, x, wl, rows, a, t) {
  const w = spr.width, h = spr.height;
  rows = Math.min(rows, EDGE - wl - 1, h);
  for (let j = 0; j < rows; j++) {
    const off = Math.round(Math.sin(TAU * 40 * t / LOOP + j * 0.7) * (0.5 + j * 0.15));
    ctx.globalAlpha = a * (1 - j / rows);
    ctx.drawImage(spr, 0, h - 1 - j, w, 1, x + off, wl + 1 + j, w, 1);
  }
  ctx.globalAlpha = 1;
}
function drawAbra(t) {
  const b = ABRAS.find(q => t >= q.a && t < q.b);
  if (!b) return;
  const dir = b.x1 > b.x0 ? 1 : -1, spr = ABRA_C[dir > 0 ? 0 : 1], w = spr.width, h = spr.height;
  const x = Math.round(lerp(b.x0, b.x1, (t - b.a) / (b.b - b.a))), wl = b.wl + Math.round(wave(t, 60, b.a / 90) * 0.6);
  boatReflect(spr, x, wl, 8, 0.32, t);
  const s = step(t, 1200), stern = dir > 0 ? x + 1 : x + w - 2;          // a soft, slow wake
  ctx.fillStyle = '#d8d0e8';
  for (let d = 1; d < 30; d++) { if (hash(d, s, 41) < 0.5) continue; ctx.globalAlpha = 0.4 * (1 - d / 30); ctx.fillRect(stern - dir * d, wl + (d > 12 ? 1 : 0), 1, 1); }
  ctx.globalAlpha = 1;
  ctx.drawImage(spr, x, wl - h + 1);
  for (const lx of ABRA_LANTERNS) {
    const X = dir > 0 ? x + lx : x + w - 1 - lx, f = 0.75 + 0.25 * hash(lx, step(t, 960), 3);
    halo(HL.abraLamp, X, wl - h + 4, f);
    streak(ctx, t, X, 3, '#ffc868', 0.3 * f, lx, wl + 1, EDGE);
  }
}

/* ================= Our terrace: sandstone ledge on the water, warm tiles, Sadu cushions, a low brass table ================= */
const LANTS = [                                      // brass pierced lanterns: floor ones, one on the café wall, one in the palm
  { id: 0, x: 84, y: 262, size: 1, floor: { rx: 46, ry: 9, n: 70 } },
  { id: 1, x: 268, y: 258, size: 0, floor: { rx: 30, ry: 7, n: 44 } },
  { id: 2, x: 428, y: 196, size: 0, hang: 176, wall: { r: 26, n: 46 } },
  { id: 3, x: 62, y: 112, size: 0, hang: 70 },
];
const TABLE = { cx: 178, y: 241, rx: 25, ry: 4 };
const DALLAH = { x: 159, y: 224 };
const CUPS = [179, 185, 191];
const RUG = { y0: 237, y1: 258, b0: 104, b1: 252, f0: 88, f1: 268 };
const SHISHA = { x: 292, top: 228 };
const POUF = { x: 314, y: 250 };                    // the smoker's leather pouf: centre and seat row
const HOSE_END = [309, 247];                        // where the hose meets the smoker's hand at rest
const MAB = { x: 350, y: 246 };                     // a mabkhara: a wooden incense burner with a glowing coal
const CUSHIONS = [{ x0: 100, x1: 146 }, { x0: 212, x1: 258 }];
const FLOOR_RAMP = ['#140e14', '#1c1318', '#26191c', '#321f20', '#3e2724', '#4c3028', '#5c3a2c', '#6e462e'];
/** static warm light the lanterns bake into the floor and the ledge (the flicker is added per frame with halos) */
const lampLight = (x, y) => LANTS.reduce((s, l) => s + Math.max(0, 1 - Math.hypot((x - l.x) / 70, (y - (l.floor ? l.y - 4 : l.y + 30)) / 26)) * (l.floor ? 0.5 : 0.3), 0);
function renderTerrace() {
  const p = pix(W, H), r = rng(64);
  // ledge: a sandstone coping on the water, lit where the lanterns reach it
  for (let x = 0; x < CAFE.x; x++) {
    const g = Math.min(1, lampLight(x, EDGE + 6));
    p.set(x, EDGE, pick(['#6a5040', '#a8845c', '#d8b280', '#f0cc98'], 0.3 + g * 0.8, x, EDGE));
    p.set(x, EDGE + 1, pick(['#4a3428', '#7a5a3e', '#a8845c'], 0.3 + g * 0.8, x, EDGE + 1));
    for (let y = EDGE + 2; y < FLOOR - 1; y++) p.set(x, y, pick(['#2a1c1a', '#4a3226', '#6a4a34'], 0.2 + g * 0.7 + (vnoise(x / 3, y, 6) - 0.5) * 0.3, x, y));
    p.set(x, FLOOR - 1, '#140e12');
    if (x % 18 === 0) for (let y = EDGE; y < FLOOR - 1; y++) p.set(x, y, '#2a1c18');
  }
  // floor: big sandstone tiles in perspective
  for (let y = FLOOR; y < H; y++) for (let x = 0; x < W; x++) {
    const d = y - FLOOR, g = lampLight(x, y);
    let v = 0.2 + d / 60 * 0.12 + g * 0.9 + (hash(x >> 3, y >> 2, 9) - 0.5) * 0.12;
    const row = [0, 4, 9, 15, 22, 30].includes(d);
    const jx = (x - 240) / ((y - 186) / 47) / 18, joint = Math.abs(jx - Math.round(jx)) * 18 * ((y - 186) / 47) < 0.5;
    if (row || joint) v -= 0.2;
    p.set(x, y, pick(FLOOR_RAMP, v, x, y));
  }
  // a rug under the majlis: indigo border with gold dots, a red field, a medallion, a fringe
  for (let y = RUG.y0; y <= RUG.y1; y++) {
    const v = (y - RUG.y0) / (RUG.y1 - RUG.y0), xl = Math.round(lerp(RUG.b0, RUG.f0, v)), xr = Math.round(lerp(RUG.b1, RUG.f1, v));
    for (let x = xl; x <= xr; x++) {
      const u = (x - xl) / (xr - xl), g = lampLight(x, y) * 0.5, eu = Math.min(u, 1 - u) * (xr - xl), ev = Math.min(y - RUG.y0, RUG.y1 - y);
      let col;
      if (eu < 1 || ev < 1) col = '#140a10';
      else if (eu < 5 || ev < 3) col = (eu >= 2 && eu < 3) || (ev === 1 && eu >= 2) ? ((x + y) % 3 ? '#c8963c' : '#6a4a20') : pick(['#141838', '#1e2448', '#2a3260'], 0.4 + g, x, y);
      else if (eu < 6 || ev < 4) col = '#d8a848';
      else {
        const dm = Math.abs(u - 0.5) * 4.2 + Math.abs(v - 0.5) * 2;
        if (dm < 0.55) col = dm > 0.47 ? '#d8a848' : dm < 0.2 ? '#b0302a' : pick(['#1a2048', '#2a3462'], 0.4 + g, x, y);
        else if ((Math.round(u * 16) + Math.round(v * 5)) % 2 === 0 && Math.abs(u * 16 - Math.round(u * 16)) < 0.12 && Math.abs(v * 5 - Math.round(v * 5)) < 0.2) col = '#d8a848';
        else col = pick(['#4a0e16', '#6a1820', '#8a2228', '#a8302c'], 0.35 + g + (vnoise(x / 3, y, 13) - 0.5) * 0.2, x, y);
      }
      p.set(x, y, col);
    }
    if (y === RUG.y1) for (let x = xl + 1; x < xr; x += 2) { p.set(x, y + 1, '#d8c8a8'); p.set(x, y + 2, '#8a7a62'); }
  }
  // Sadu cushions on the rug: a long box cushion (plain top, woven front) and a round bolster behind it
  for (const { x0, x1 } of CUSHIONS) {
    for (let y = 237; y < 243; y++) for (let x = x0 + 3; x <= x1 - 3; x++) {
      const j = y - 237, end = x < x0 + 5 || x > x1 - 5;
      if ((j === 0 || j === 5) && (x < x0 + 4 || x > x1 - 4)) continue;
      let col = ['#e0503c', '#b82e2c', '#a02428', '#861c22', '#6a141a', '#480c12'][j];
      if (end) col = j % 2 ? '#1a0e10' : '#e8dcc4';
      else if ((x - x0) % 12 === 6) col = j < 3 ? '#e8dcc4' : '#b8ac94';
      p.set(x, y, col);
    }
    for (let y = 243; y < 252; y++) for (let x = x0; x <= x1; x++) {
      const j = y - 243, g = lampLight(x, y) * 0.3;
      if (j === 8 && (x === x0 || x === x1)) continue;
      let col;
      if (j < 3) col = j === 0 ? '#e0503c' : pick(['#8a1a20', '#b02a2a', '#c8382e'], 0.55 - j * 0.15 + g, x, y);
      else if (j === 3 || j === 8) col = '#1a0e10';
      else if (j === 4) col = (x + 1) % 4 < 2 ? '#e8dcc4' : '#9a1e24';
      else { const k = j - 5, m = (x - x0) % 6; col = (m < 3 ? m >= k : 5 - m >= k) ? '#e8dcc4' : (m + k) % 2 ? '#1a0e10' : '#8a1a20'; }
      if (j >= 5 && j <= 7 && (x - x0) % 12 === 0) col = '#e0782a';
      p.set(x, y, col);
    }
    for (let x = x0 + 1; x < x1; x++) p.set(x, 252, '#0e080a');                   // contact shadow
  }
  // the low table: a round engraved brass tray on a folding wooden stand
  const { cx, y: ty, rx, ry } = TABLE;
  for (let y = ty + 4; y < 256; y++) {
    const k = (y - ty - 4) / 12;
    for (const [a, b] of [[cx - 20 + k * 30, cx - 20 + k * 30 + 2], [cx + 20 - k * 30 - 2, cx + 20 - k * 30]])
      for (let x = Math.round(a); x <= Math.round(b); x++) p.set(x, y, x === Math.round(a) ? '#7a4e2c' : '#3a2416');
  }
  for (let y = ty - ry; y <= ty + ry + 2; y++) for (let x = cx - rx; x <= cx + rx; x++) {
    const e = ((x - cx) / rx) ** 2 + ((Math.min(y, ty + ry) - ty) / ry) ** 2;
    if (e > 1) continue;
    let col;
    if (y > ty + ry - 1) col = y === ty + ry + 2 ? '#2a1208' : '#6a3418';                    // the rim's thickness
    else if (e > 0.78) col = y < ty ? '#f0a868' : '#9a5028';
    else col = pick(['#5a2a14', '#7a3e1e', '#9a5630', '#b86e40'], 0.5 - (x - cx) / rx * 0.3 + (e < 0.3 ? 0.12 : 0), x, y);
    if (e > 0.35 && e < 0.45 && (x & 1)) col = '#4a2210';                                   // engraved ring
    p.set(x, y, col);
  }
  // finjan cups and a bowl of dates
  for (const x of CUPS) { p.set(x, ty - 2, '#e8c060'); p.set(x + 1, ty - 2, '#5a3018'); p.set(x + 2, ty - 2, '#e8c060'); p.fill(x, ty - 1, 3, 1, '#e8e2d6'); p.set(x + 2, ty - 1, '#b8b0a4'); }
  p.fill(195, ty - 1, 7, 2, '#d8c8b0'); p.fill(196, ty + 1, 5, 1, '#8a7a68');
  for (const [dx, dy, col] of [[1, 0, '#5a2410'], [2, 0, '#7a3418'], [3, 0, '#4a1c0c'], [4, 0, '#6a2c14'], [5, 0, '#5a2410'], [2, -1, '#8a4420'], [4, -1, '#5a2410'], [3, -1, '#6a2c14']]) p.set(195 + dx, ty - 2 + dy, col);
  // a round leather pouf by the shisha, stitched in panels
  for (let y = POUF.y; y < POUF.y + 8; y++) for (let dx = -7; dx <= 7; dx++) {
    const j = y - POUF.y, hw = j === 0 || j === 7 ? 6 : 7;
    if (Math.abs(dx) > hw) continue;
    p.set(POUF.x + dx, y, j === 0 ? (dx < 2 ? '#d09a62' : '#a87444') : (dx + 9) % 4 === 0 ? '#4a2a16' : pick(['#3e2414', '#6a4024', '#8a5a34', '#a8703e'], 0.75 - (dx + 7) / 20 - j / 20, POUF.x + dx, y));
  }
  for (let dx = -6; dx <= 6; dx++) p.set(POUF.x + dx, POUF.y + 8, '#0e080a');
  MAB_ROWS.forEach((row, j) => { for (let i = 0; i < row.length; i++) { const col = MAB_KEY[row[i]]; if (col) p.set(MAB.x - 4 + i, MAB.y + j, col); } });
  for (let x = MAB.x - 4; x <= MAB.x + 4; x++) p.set(x, MAB.y + MAB_ROWS.length, '#0e080a');
  return trim(p);
}
/** the shisha: glass vase, brass stem, a clay bowl (the ember glows per frame), the hose sagging to the smoker's hand.
    Its own canvas, so the waiter can pass behind it. */
function renderShisha() {
  const p = pix(W, H), sx = SHISHA.x;
  for (let y = 246; y < 259; y++) {
    const hw = y < 249 ? 2 + (y - 246) : y > 256 ? 5 - (y - 256) : 5;
    for (let dx = -hw; dx <= hw; dx++) p.set(sx + dx, y, dx === -hw + 1 ? '#8ad8c0' : dx < 0 ? '#2a7a66' : dx === hw ? '#0e2a24' : '#1a4a40');
  }
  for (let y = SHISHA.top + 4; y < 246; y++) { p.set(sx, y, y % 5 === 0 ? '#ffe08a' : '#c8902e'); p.set(sx + 1, y, '#6a4418'); }
  for (let dx = -3; dx <= 3; dx++) { p.set(sx + dx, SHISHA.top + 10, dx < 0 ? '#f0c060' : '#8a5a1e'); }        // tray
  for (let y = SHISHA.top; y < SHISHA.top + 4; y++) for (let dx = -2 + (y - SHISHA.top > 1 ? 1 : 0); dx <= 2 - (y - SHISHA.top > 1 ? 1 : 0); dx++) p.set(sx + dx, y, dx < 0 ? '#a85a30' : '#6a3018');
  const [hx, hy] = HOSE_END;
  for (let k = 0; k <= 30; k++) {
    const u = k / 30, x = (1 - u) ** 2 * (sx + 2) + 2 * u * (1 - u) * (sx + 9) + u * u * hx, y = (1 - u) ** 2 * 240 + 2 * u * (1 - u) * 258 + u * u * hy;
    p.set(x, y, '#3a2a3a'); p.set(x, y + 1, '#1a1220');
  }
  return trim(p);
}
const MAB_ROWS = ['..yBBBd..', '..DaaaD..', '.yBBBBBd.', '..WWwWW..', '...WwW...', '...WwW...', '..WWWWW..', '.WwMWMWW.', '.WMwWWMW.', '.WwMWMWW.', '.WWWWWWW.', '.W.....W.'];
const MAB_KEY = { a: '#3a2016', y: '#ffe08a', B: '#c8902e', D: '#6a4418', W: '#4a2616', w: '#7a4428', M: '#e0d0a8' };
/* the dallah: a brass Arabic coffee pot with a crescent beak and a tall finial */
const DALLAH_ROWS = [
  '......y......', '.....yBd.....', '......B......', '....yBBBd....', '.....DDD.....', '....yBBBd..h.', 'b....yBd...h.',
  '.b...yBd....h', '..b.yYBBd...h', '...byYBBBd..h', '...yYBBBBDd.h', '...yYBBBBDdh.', '...yBBBBBDd..', '....yBBBDd...',
  '.....DBDD....', '....yBBBBd...', '....DDDDDD...'];
const DALLAH_KEY = { y: '#fff0b0', Y: '#ffd870', B: '#d8a040', D: '#8a5a22', d: '#5a3a18', b: '#e8b050', h: '#6a4418' };
/** a brass pierced lantern: ring, domed cap, a body pierced with a star lattice (the holes glow), a base */
function makeLantern(size, lit) {
  const w = size ? 13 : 9, bh = size ? 12 : 8, dome = size ? 5 : 3, h = 3 + dome + bh + 3, p = pix(w, h), m = (w - 1) / 2;
  p.set(m, 0, '#c8902e'); p.set(m - 1, 1, '#c8902e'); p.set(m + 1, 1, '#8a5a1e'); p.set(m, 2, '#f0c060');
  for (let j = 0; j < dome; j++) { const hw = 1 + Math.round((j + 1) / dome * (m - 1)); for (let dx = -hw; dx <= hw; dx++) p.set(m + dx, 3 + j, dx === -hw ? '#ffe08a' : dx > hw - 2 ? '#6a4418' : '#c8902e'); }
  const y0 = 3 + dome;
  for (let j = 0; j < bh; j++) for (let i = 0; i < w; i++) {
    const rib = i === 0 || i === w - 1 || i === Math.round(m) || j === 0 || j === bh - 1;
    const hole = !rib && i % 2 === 1 && j % 2 === (Math.floor(i / 2) % 2);
    p.set(i, y0 + j, rib ? (i === 0 ? '#ffd870' : i === w - 1 ? '#5a3a18' : '#b07c2a') : hole ? (lit ? (j < bh / 2 ? '#fff4c8' : '#ffc860') : '#6a3a14') : (i < m ? '#8a5a1e' : '#5a3a18'));
  }
  for (let j = 0; j < 3; j++) { const hw = Math.round(m) - (j === 2 ? 1 : 0); for (let dx = -hw; dx <= hw; dx++) p.set(m + dx, y0 + bh + j, j === 0 ? '#e8b050' : dx < 0 ? '#a87028' : '#5a3a18'); }
  return p.done();
}
/** dapple: the star lattice's little spots of light on the floor (or the wall) around a lantern (build time) */
function lanternDots(l) {
  const r = rng(700 + l.id), dots = [];
  if (l.floor) for (let k = 0; k < l.floor.n; k++) {
    const a = r() * TAU, d = 0.25 + Math.sqrt(r()) * 0.75, x = l.x + Math.cos(a) * d * l.floor.rx, y = l.y - 2 + Math.sin(a) * d * l.floor.ry;
    if (y < FLOOR + 1 || y > H - 2) continue;
    dots.push({ x: Math.round(x), y: Math.round(y), d, w: d > 0.45 && r() < 0.6 ? 2 : 1, k: 3 + Math.floor(r() * 6) });
  }
  if (l.wall) for (let k = 0; k < l.wall.n; k++) {
    const a = r() * TAU, d = 0.3 + Math.sqrt(r()) * 0.7, x = l.x + Math.cos(a) * d * l.wall.r, y = l.y + Math.sin(a) * d * l.wall.r * 1.2;
    if (x < CAFE.x + 1 || y < CAFE.top + 2 || y > FLOOR) continue;
    dots.push({ x: Math.round(x), y: Math.round(y), d, w: 1, k: 3 + Math.floor(r() * 6) });
  }
  return dots;
}
const flick = (t, s) => 0.84 + 0.08 * wave(t, 97 + s * 13, s * 0.3) + 0.08 * (hash(s, step(t, 2400), 5) - 0.5) * 2;
const lanternSway = (l, t) => l.hang ? Math.round(wave(t, 16 + l.id * 3, l.id * 0.2) * 0.8 + wave(t, 41, l.id * 0.5) * 0.4) : 0;
function drawLanterns(t, sel) {
  for (const l of LANTS) {
    if (!sel(l)) continue;
    const f = flick(t, l.id), sw = lanternSway(l, t), spr = LANT_C[l.size], w = spr[0].width, h = spr[0].height;
    const x = Math.round(l.x - (w >> 1) + sw), y = l.y - h;
    if (l.hang) { ctx.fillStyle = '#3a2a1a'; for (let yy = l.hang; yy < y; yy++) ctx.fillRect(Math.round(l.x + sw * (yy - l.hang) / (y - l.hang)), yy, 1, 1); }
    halo(l.size ? HL.lantBig : HL.lant, x + (w >> 1), y + h * 0.55, f * 0.9);
    ctx.drawImage(spr[0], x, y);
    ctx.globalAlpha = clamp((f - 0.7) / 0.3, 0, 1); ctx.drawImage(spr[1], x, y); ctx.globalAlpha = 1;
    halo(HL.lantCore, x + (w >> 1), y + h * 0.6, f * 0.8);
    if (!DOTS[l.id].length) continue;
    ctx.fillStyle = '#ffd890';
    for (const d of DOTS[l.id]) {
      ctx.globalAlpha = Math.min(1, 0.85 * f * (1.15 - d.d)) * (0.8 + 0.2 * wave(t, d.k * 17, d.d));
      ctx.fillRect(d.x - sw * Math.round(d.d * 2), d.y, d.w, 1);
    }
    ctx.globalAlpha = 1;
  }
}

/* ================= Our café: a sandstone wall with crenellations and a pointed arch, warm inside ================= */
const ARCH = { cx: 457, hw: 16, spring: 199, apex: 178, sill: 262 };
const archHW = y => y >= ARCH.spring ? ARCH.hw : ARCH.hw * (1 - Math.pow((ARCH.spring - y) / (ARCH.spring - ARCH.apex), 1.7));
const QAHWA = { x: 448, y: 170 };
function renderCafe() {
  const p = pix(W, H);
  for (let x = CAFE.x; x < W; x++) {
    const m = (x - CAFE.x) % 7;                        // stepped triangular merlons
    for (let y = CAFE.top - 5; y < CAFE.top; y++) if (Math.abs(m - 3) <= y - (CAFE.top - 5) - 1 + 1 && m < 7) p.set(x, y, Math.abs(m - 3) === y - (CAFE.top - 5) ? '#e0b47a' : '#9a7048');
    for (let y = CAFE.top; y < H; y++) {
      const lamp = Math.max(0, 1 - Math.hypot((x - 428) / 40, (y - 190) / 48)), door = Math.max(0, 1 - Math.hypot((x - ARCH.cx) / 34, (y - 225) / 60));
      let v = 0.2 + lamp * 0.55 + door * 0.35 + (vnoise(x / 5, y / 3, 21) - 0.5) * 0.08 + (x < CAFE.x + 2 ? 0.2 : 0);
      if ((y - CAFE.top) % 6 === 5 || (x + (Math.floor((y - CAFE.top) / 6) % 2) * 6) % 12 === 0) v -= 0.1;   // block joints
      p.set(x, y, pick(STONE, v, x, y));
    }
    p.set(x, CAFE.top, '#e0b47a'); p.set(x, CAFE.top + 1, '#7a5a3c');
  }
  // the arch: a carved stone frame, a warm interior with a lattice screen, shelves and a lamp
  for (let y = ARCH.apex - 2; y < ARCH.sill + 1; y++) {
    const hw = archHW(Math.max(y, ARCH.apex));
    for (let dx = -Math.ceil(hw) - 2; dx <= Math.ceil(hw) + 2; dx++) {
      const x = ARCH.cx + dx, inside = y >= ARCH.apex && Math.abs(dx) <= hw, frame = !inside && Math.abs(dx) <= hw + 2 && y >= ARCH.apex - 2;
      if (x >= W) continue;
      if (frame) { p.set(x, y, dx < 0 ? '#f0cc90' : '#b08050'); continue; }
      if (!inside) continue;
      let col = pick(['#b8642a', '#e08c3c', '#ffb860', '#ffd890', '#fff0c0'], 0.95 - (y - ARCH.apex) / 84 - Math.abs(dx) / 60, x, y);
      if (y < 204 && ((x + y) % 4 === 0 || (x - y + 400) % 4 === 0)) col = '#7a4420';            // mashrabiya lattice
      if ((y === 222 || y === 236) && dx > 2) col = '#6a3a1a';                                 // shelves
      if ((y === 219 || y === 220 || y === 233 || y === 234) && dx > 3 && dx % 3) col = dx % 2 ? '#c84a2a' : '#3a6a8a';   // tins of coffee and cardamom
      if (y >= 244) col = pick(['#5a2e18', '#8a4a24', '#b86a30'], 0.8 - (y - 244) / 30, x, y);   // the floor inside
      p.set(x, y, col);
    }
  }
  for (let dx = -ARCH.hw - 2; dx <= ARCH.hw + 2; dx++) p.set(ARCH.cx + dx, ARCH.sill + 1, '#d8b07a');
  for (let y = ARCH.apex + 18; y < ARCH.apex + 26; y++) p.set(ARCH.cx, y, '#3a2410');           // the inside lamp's chain
  // wall lantern bracket
  for (let x = 424; x < 433; x++) p.set(x, 174, x === 424 ? '#6a4418' : '#3a2a1a');
  p.set(432, 175, '#3a2a1a');
  return trim(p);
}
function drawCafe(t) {
  // someone crosses the café inside now and then; the inside lamp glows; the QAHWA neon hums and sputters
  for (const [a, b, d] of [[28, 36, 1], [131, 138, -1], [207, 215, 1]]) {
    if (t < a || t >= b) continue;
    const u = (t - a) / (b - a), x = Math.round(ARCH.cx + (d > 0 ? lerp(-18, 18, u) : lerp(18, -18, u)));
    const bob = step(t, 480) & 1;
    ctx.fillStyle = '#4a2410';
    for (let y = 214 + bob; y < 244; y++) { const hw = y < 219 + bob ? 1 : y < 222 ? 2 : 3; for (let dx = -hw; dx <= hw; dx++) if (Math.abs(x + dx - ARCH.cx) <= archHW(y)) ctx.fillRect(x + dx, y, 1, 1); }
  }
  rect(ctx, ARCH.cx - 2, ARCH.apex + 26, 5, 3, '#ffe8a8'); halo(HL.inLamp, ARCH.cx, ARCH.apex + 28, 0.8 + 0.1 * wave(t, 120));
  const nf = inFlick(t) * (0.92 + 0.08 * wave(t, 2400, 0.2));
  if (nf > 0.1) { text(ctx, 'QAHWA', QAHWA.x, QAHWA.y, nf > 0.6 ? '#ffd27a' : '#8a5a2a'); halo(HL.neon, QAHWA.x + 9, QAHWA.y + 2, nf * 0.8); }
  else text(ctx, 'QAHWA', QAHWA.x, QAHWA.y, '#3a2818');
}
const NEON_FLICK = [[58, 59.6], [141.2, 142], [219, 220.8]];
function inFlick(t) {
  for (const [a, b] of NEON_FLICK) if (t >= a && t < b) { const h = hash(7, Math.floor(t * 24), 3); return h > 0.55 ? 1 : h > 0.3 ? 0.4 : 0; }
  return 1;
}

/* ================= The date palms: scaly trunks wrapped in fairy lights, big fronds swaying in the breeze ================= */
const POSES = 7;
function palmTrunk(P) {                               // centre x and half-width per row, from the crown down to the planter
  const rows = [];
  for (let y = P.ty; y <= P.by; y++) {
    const u = (y - P.ty) / (P.by - P.ty), cx = lerp(P.tx, P.bx, u) + Math.sin(u * Math.PI) * 3;
    rows.push({ y, cx, hw: 3 + u * 1.6 });
  }
  return rows;
}
function renderPalm(P, seed, lampX) {
  const p = pix(W, H), bulbs = [], r = rng(seed);
  for (const { y, cx, hw } of palmTrunk(P)) {
    for (let x = Math.floor(cx - hw); x <= Math.ceil(cx + hw); x++) {
      const dx = x - cx, side = dx / hw;
      if (Math.abs(side) > 1.05) continue;
      const lit = Math.max(0, 1 - Math.abs(x - lampX) / 80) * smooth(120, 250, y);
      let v = 0.3 + 0.25 * (lampX > cx ? side : -side) * lit + lit * 0.4 - smooth(160, 60, y) * 0.15;
      const hatch = ((y + dx * 1.2) % 5 + 5) % 5 < 1 || ((y - dx * 1.2) % 5 + 5) % 5 < 1;
      if (hatch) v -= 0.22;
      if (Math.abs(side) > 0.8) v -= 0.12;
      p.set(x, y, pick(['#1a120e', '#2a1c14', '#3e2a1c', '#5a3e26', '#7a5634', '#9a7044'], v, x, y));
    }
    // a helix of fairy lights on its wire
    const th = y * 0.5 + seed, s = Math.sin(th);
    if (Math.cos(th) > 0 && y > P.ty + 16 && y < P.by - 8) {
      const x = Math.round(cx + s * hw * 0.95);
      p.set(x, y, '#2a2018');
      if (y % 4 === 0) bulbs.push({ x, y, k: 20 + Math.floor(r() * 40), ph: r(), id: bulbs.length });
    }
  }
  // the crown's boot of old leaf bases and a planter
  for (let y = P.ty - 3; y < P.ty + 8; y++) for (let dx = -6; dx <= 6; dx++) if (Math.abs(dx) < 6 - Math.abs(y - P.ty - 2) * 0.4) p.set(P.tx + dx, y, (dx + y) % 3 ? '#3a2818' : '#5a3e24');
  for (let y = P.by; y < P.by + 14; y++) for (let dx = -10; dx <= 10; dx++) {
    const hw = y === P.by ? 10 : 9 - Math.floor((y - P.by) / 6);
    if (Math.abs(dx) > hw) continue;
    p.set(P.bx + dx, y, y === P.by ? '#e0b47a' : y === P.by + 1 ? '#7a5a3c' : pick(STONE, 0.5 - dx / 24 + Math.max(0, 1 - Math.abs(P.bx + dx - lampX) / 90) * 0.3, P.bx + dx, y));
  }
  return { c: trim(p), bulbs };
}
/** one frond: a rachis arching out and bending under gravity, stiff leaflets along it in a V (build time).
    dry: an old brown frond hanging down the trunk. warm: lantern light catches its underside. */
function frondPix(len, ang, grav, seed, pose, warm, dry) {
  const S = len * 2 + 24, o = S >> 1, p = pix(S, S);
  let x = o, y = o, dx = Math.cos(ang + pose * 0.06), dy = Math.sin(ang + pose * 0.06);
  const LEAF = dry ? ['#1e140e', '#2e1e12', '#4a3218'] : ['#08120c', '#0e1c14', '#16281c'];
  const RIM = dry ? '#5a4024' : '#2a4630', WARM_TIP = '#3e3418';
  for (let s = 0; s < len; s++) {
    const f = s / len;
    dy += grav * (0.5 + f * 1.4) * (1 + pose * 0.1); const n = Math.hypot(dx, dy); dx /= n; dy /= n;
    x += dx; y += dy;
    if (f < 0.08) { p.set(x, y, LEAF[2]); continue; }
    const L = Math.max(1, Math.round(Math.pow(Math.sin(Math.PI * Math.min(1, f * 1.15 + 0.08)), 0.7) * (dry ? 6 : 10) * (s % 3 ? 0.82 : 1)));
    const a0 = Math.atan2(dy, dx);
    for (const sd of [-1, 1]) {
      const a = a0 + sd * (0.62 + f * 0.25), ux = Math.cos(a), uy = Math.sin(a);
      for (let k = 1; k <= L; k++) {
        const q = k / L, X = x + ux * k, Y = y + uy * k + q * q * L * 0.45;      // leaflets droop at the ends
        const up = uy + q * 0.45 < -0.2;
        let col = LEAF[q < 0.35 ? 2 : q < 0.75 ? 1 : 0];
        if (k === L && up) col = RIM;                                           // a cool rim where the sky catches them
        else if (k === L && !up && warm && f > 0.3 && s % 2) col = WARM_TIP;
        p.set(X, Y, col);
      }
    }
    p.set(x, y, f < 0.6 ? (dry ? '#6a4a28' : '#2e3e24') : LEAF[2]);                // the rachis
  }
  return trim(p);
}
function makeCrown(P, seed, fronds) {
  return fronds.map(([ang, len, grav, dry], i) => {
    const poses = [], o = len + 12;
    for (let k = 0; k < POSES; k++) {
      const q = frondPix(len, ang, grav, seed + i, (k / (POSES - 1)) * 2 - 1, Math.sin(ang) > -0.4, dry);
      poses.push({ c: q.c, x: q.x - o + P.tx, y: q.y - o + P.ty });
    }
    return { poses, k: 9 + (i * 5) % 11, ph: hash(seed, i, 1), ph2: hash(seed, i, 2) };
  });
}
// [angle, length, gravity, dry]: drawn in order, so the hanging dry fronds go first and the top ones last
const FRONDS_L = [[1.35, 26, 0.004, 1], [1.75, 24, 0.004, 1], [2.75, 50, 0.034], [0.35, 52, 0.036], [-3.05, 56, 0.022], [-0.05, 60, 0.03],
  [-2.6, 58, 0.02], [-0.5, 58, 0.024], [-2.15, 50, 0.02], [-0.95, 52, 0.022], [-1.75, 44, 0.018], [-1.35, 44, 0.02]];
const FRONDS_R = [[1.4, 24, 0.004, 1], [1.8, 26, 0.004, 1], [2.8, 50, 0.036], [0.4, 48, 0.036], [-3.0, 56, 0.022], [-0.1, 54, 0.03],
  [-2.55, 54, 0.02], [-0.55, 54, 0.024], [-2.1, 48, 0.02], [-1.0, 50, 0.022], [-1.7, 42, 0.018], [-1.3, 42, 0.02]];
function drawCrown(fr, t, gust) {
  for (const f of fr) {
    const sw = wave(t, f.k, f.ph) * 0.6 + wave(t, f.k * 3 + 1, f.ph2) * 0.25 + gust * 0.5;
    const q = f.poses[clamp(Math.round((sw + 1) / 2 * (POSES - 1)), 0, POSES - 1)];
    ctx.drawImage(q.c, q.x, q.y);
  }
}
const gustAt = t => 0.5 * wave(t, 7, 0.15) * (0.6 + 0.4 * wave(t, 17, 0.4));
function drawBulbs(bulbs, t, salt) {
  const s = step(t, 960);
  for (const b of bulbs) {
    let a = 0.6 + 0.4 * wave(t, b.k, b.ph);
    if (hash(b.id + salt, s, 3) < 0.06) a *= 0.25;
    ctx.globalAlpha = a; ctx.fillStyle = b.id % 3 ? '#fff0c0' : '#ffd890'; ctx.fillRect(b.x, b.y, 1, 1);
    if (b.id % 3 === 0) { ctx.globalAlpha = 1; halo(HL.bulb, b.x, b.y, a * 0.7); }
  }
  ctx.globalAlpha = 1;
}

/* ================= A street cat on the ledge, watching the fountain ================= */
const CAT = { x: 338, y: EDGE - 13 };
const CAT_ROWS = ['.#.....#...', '.##...##...', '.#######...', '#########..', '.#######...', '..#####....', '..######...', '.########..',
  '.#########.', '##########.', '###########', '###########', '.#########.'];
function makeCat() {
  const h = CAT_ROWS.length, w = CAT_ROWS[0].length, p = pix(w, h);
  const on = (i, j) => j >= 0 && j < h && i >= 0 && i < w && CAT_ROWS[j][i] === '#';
  for (let j = 0; j < h; j++) for (let i = 0; i < w; i++) {
    if (!on(i, j)) continue;
    let col = '#140e14';
    if (!on(i, j - 1)) col = j < 2 ? '#a88ac0' : '#6a5a8a';                      // cool rim from the lake's glow
    else if (!on(i - 1, j) && j > 6) col = '#8a5a34';                           // warm rim from the lantern
    else if (!on(i + 1, j) && j < 9) col = '#2a2238';
    p.set(i, j, col);
  }
  return p.done();
}
const EAR_FLICKS = [19.4, 61.8, 101.2, 146.5, 181.7, 227.1];
const HEAD_TURNS = [[44, 52], [118, 126], [168, 176]];    // it turns to the fountain's shooters
function drawCat(t) {
  const X = CAT.x, Y = CAT.y, turn = HEAD_TURNS.find(([a, b]) => t >= a && t < b), hd = turn ? (t < (turn[0] + turn[1]) / 2 ? -1 : 1) : 0;
  ctx.drawImage(CAT_C, 0, 5, 11, 8, X, Y + 5, 11, 8);
  ctx.drawImage(CAT_C, 0, 0, 11, 5, X + hd, Y, 11, 5);
  if (EAR_FLICKS.some(e => t >= e && t < e + 0.35)) { rect(ctx, X + 7 + hd, Y, 1, 1, '#2a2238'); rect(ctx, X + 8 + hd, Y + 1, 1, 1, '#a88ac0'); }
  const sw = 0.8 * wave(t, 23, 0.3) + 0.4 * wave(t, 61, 0.1);
  ctx.fillStyle = '#140e14';
  for (let j = 0; j < 10; j++) ctx.fillRect(X + 9 + Math.round(sw * j * j / 30), EDGE - 1 + j, 1 + (j < 2 ? 1 : 0), 1);
}

/* ================= The Burj's LED facade shows (loop seconds): colour washes, fills and sweeps up the tower ================= */
const LED_COLS = ['#c77dff', '#ffc34a', '#3ee0d0', '#ff4fa0', '#f4f0ff', '#5a7aff'];
const LED_SHOWS = [
  { a: 112, b: 146, pats: ['rise', 'split', 'fill', 'pulse', 'sparkle', 'rise'], pal: [0, 3, 5, 2] },
  { a: 160, b: 200, pats: ['fill', 'sparkle', 'rise', 'split', 'pulse', 'fill'], pal: [4, 1, 0, 1] },   // with the fountain: white and gold
];
LED_SHOWS.forEach(s => {
  s.cols = s.pal.map(i => LED_COLS[i]);
  s.ramp = [];
  for (let i = 0; i < 48; i++) { const f = i / 48 * s.cols.length, k = Math.floor(f); s.ramp.push(mixHex(s.cols[k], s.cols[(k + 1) % s.cols.length], f - k)); }
});
const ledLevel = t => Math.max(...LED_SHOWS.map(s => env(t, s.a, s.b, 1.2)));
function ledState(t) {
  const S = LED_SHOWS.find(s => t >= s.a && t < s.b);
  if (!S) return null;
  const u = t - S.a, seg = (S.b - S.a) / S.pats.length, pi = Math.min(S.pats.length - 1, Math.floor(u / seg)), v = u - pi * seg;
  const ci = Math.floor(v / 2.8 + pi) % S.pal.length;
  return { S, kind: S.pats[pi], v, main: S.pal[ci], ci };
}
let LC, lx;                                          // scratch canvas for the facade colours
function drawBurjShow(st, lv, t) {
  if (!st || lv < 0.01) return;
  const { S, kind, v, ci } = st, { w, h } = BK_M, cols = S.cols, n = cols.length, ramp = S.ramp;
  lx.globalCompositeOperation = 'source-over'; lx.globalAlpha = 1; lx.clearRect(0, 0, w, h);
  if (kind === 'rise') for (let j = 0; j < h; j++) { lx.fillStyle = ramp[Math.floor(frac((1 - j / h) * 1.3 - v * 0.42) * ramp.length) % ramp.length]; lx.fillRect(0, j, w, 1); }
  else if (kind === 'fill') {
    const lvl = frac(v / 2.8) * 1.15, A = cols[ci], B = cols[(ci + n - 1) % n];
    for (let j = 0; j < h; j++) { const hf = 1 - j / h; lx.fillStyle = Math.abs(hf - lvl) < 0.015 ? '#ffffff' : hf < lvl ? A : B; lx.fillRect(0, j, w, 1); }
  } else if (kind === 'split') {
    const sw = Math.floor(v / 0.7) & 1, A = cols[(ci + sw) % n], B = cols[(ci + 1 - sw + n) % n];
    lx.fillStyle = A; lx.fillRect(0, 0, 14, h); lx.fillStyle = B; lx.fillRect(14, 0, w - 14, h);
    const y = Math.round(h - frac(v / 1.4) * h); lx.fillStyle = '#ffffff'; lx.fillRect(0, y, w, 2);
  } else if (kind === 'pulse') {
    lx.fillStyle = mixHex(cols[ci], cols[(ci + 1) % n], 0.5 + 0.5 * Math.sin(v * 5)); lx.fillRect(0, 0, w, h);
    const y = Math.round(h - frac(v / 1.8) * (h + 12)); lx.fillStyle = 'rgba(255,255,255,0.8)'; lx.fillRect(0, y, w, 6);
  } else {
    lx.globalAlpha = 0.45; lx.fillStyle = cols[ci]; lx.fillRect(0, 0, w, h); lx.globalAlpha = 1;   // a tint, the tower twinkling through it
    const gs = step(t, 2400);
    for (let k = 0; k < 150; k++) { lx.fillStyle = k % 3 ? '#fff0c8' : '#ffffff'; lx.fillRect(Math.floor(hash(k, gs, 91) * w), Math.floor(hash(k, gs, 92) * h), 1, 1); }
  }
  lx.globalCompositeOperation = 'destination-in'; lx.drawImage(BURJ.mask, 0, 0);
  ctx.globalAlpha = lv * 0.88; ctx.drawImage(LC, BK_M.x, BK_M.y); ctx.globalAlpha = 1;
}

/* ================= Sand: fine desert grains on a warm breeze at three depths, glinting in the lamps ================= */
const SAND = [
  { count: 150, nMin: 3, nMax: 5, y0: 30, y1: 176, amp: 3, fall: 14, a: 0.22, col: '#b08878', litA: 0.55, size: 1 },
  { count: 90, nMin: 6, nMax: 9, y0: 50, y1: 228, amp: 5, fall: 24, a: 0.4, col: '#d8b088', litA: 0.85, size: 1 },
  { count: 46, nMin: 11, nMax: 15, y0: 70, y1: 266, amp: 8, fall: 36, a: 0.55, col: '#ecc89a', litA: 0.95, size: 2 },
];
SAND.forEach((L, li) => {
  const r = rng(900 + li);
  L.g = [];
  for (let i = 0; i < L.count; i++) L.g.push({ id: i + li * 1000, n: L.nMin + Math.floor(r() * (L.nMax - L.nMin + 1)), ph: r(), kb: 20 + Math.floor(r() * 40), pb: r() });
});
const LIGHTS = [
  { x: 84, y: 246, rx: 46, ry: 26, col: '#ffcf80' }, { x: 268, y: 248, rx: 30, ry: 20, col: '#ffcf80' },
  { x: 428, y: 188, rx: 28, ry: 28, col: '#ffcf80' }, { x: 62, y: 104, rx: 24, ry: 24, col: '#ffcf80' },
  { x: BK.cx, y: 100, rx: 18, ry: 72, col: '#dfe6ff' }, { x: 90, y: 168, rx: 90, ry: 14, col: '#ffc070' },
  { x: ARCH.cx, y: 222, rx: 24, ry: 42, col: '#ffc878' },
];
const LIGHTS_BB = [...LIGHTS, { x: BB.x + (BB.w >> 1), y: BB.y + (BB.h >> 1), rx: 34, ry: 20, col: '#ff9a7a' }];   // + the hoarding
function litCol(x, y, lights) {
  for (const l of lights) { const dx = (x - l.x) / l.rx, dy = (y - l.y) / l.ry; if (dx * dx + dy * dy < 1) return l.col; }
  return null;
}
function drawSand(L, t, lights, gust) {
  const span = W + 24, gs = step(t, 1200);
  for (const g of L.g) {
    const { cyc, u } = cycle(t, g.n, g.ph);
    const x = Math.round(W + 12 - u * span - gust * L.amp * 2);
    if (x < -2 || x > W) continue;
    const y = Math.round(L.y0 + Math.pow(hash(g.id, cyc, 2), 0.6) * (L.y1 - L.y0 - L.fall) + u * L.fall + L.amp * Math.sin(TAU * (g.kb * t / LOOP + g.pb)));
    const lit = litCol(x, y, lights), glint = hash(g.id, gs, 5) < (lit ? 0.12 : 0.03);
    ctx.fillStyle = glint ? '#fff6dc' : lit || L.col; ctx.globalAlpha = glint ? 0.95 : lit ? L.litA : L.a;
    ctx.fillRect(x, y, glint ? 1 : L.size, 1);
  }
  ctx.globalAlpha = 1;
}
// a picked weather (site/js/engine/weather.js): Dubai's own falling thing is sand, so every pick hides it; rain, snow and
// leaves land on the far shore, on the lake and on our terrace floor, and catch the same lamps as the sand
const WEATHER = weatherPlans(['sand']);
const WX_LAND = [[150, WL - 1], [WL + 3, EDGE - 2], [FLOOR + 3, H - 4]];
const WX = { land: WX_LAND, lights: LIGHTS };
const WX_BB = { land: WX_LAND, lights: LIGHTS_BB };

/* ================= Timed events (loop seconds) ================= */
const SPLASHES = SHOWS.flatMap(S => {
  const sh = S.secs.find(s => s[0] === 'shoot'), fi = S.secs.find(s => s[0] === 'finale');
  return [...SHOT_ORDER.map((k, i) => ({ t: +(S.a + sh[1] + i * SHOT_GAP).toFixed(2), x: SHOOT[k], gain: k === 3 ? 0.8 : i % 2 ? 0.5 : 0.6 })),
    { t: +(S.a + fi[1] + 0.2).toFixed(2), x: BK.cx, gain: 0.9 }];
});
const blinkI = t => { const f = frac(t / 1.5); return f < 0.1 ? f / 0.1 : Math.max(0, 1 - (f - 0.1) / 0.45); };   // 160 per loop

/* ================= Pre-render everything static ================= */
let HL, SKY_C, MOON_C, HAZES, DUNE_BACK, DUNE_FRONT, CAMEL_C, FAR, TWR, BURJ, VIA_C, STN_F, TRAIN_C, NEAR_C, BBF_C, BB_AD, BB_AD_HI, BB_LINE_C, PITCH, WATER_C, MIST_C,
  ABRA_C, TERR_C, SHISHA_C, DALLAH_C, LANT_C, DOTS, CAFE_C, PALM_LC, PALM_RC, CROWN_L, CROWN_R, CAT_C, PUFF_C, VIG_C;
function prerender() {
  HL = {
    red: makeHalo(5, '#ff3030', 0.6, 1), strobe: makeHalo(4, '#f0f4ff', 0.6, 1), moon: makeHalo(26, '#d8c49a', 0.14, 1.6),
    bk: makeHalo(80, '#8a98ff', 0.1, 1.5), bkTop: makeHalo(22, '#e8f0ff', 0.3), tip: makeHalo(6, '#ffffff', 0.4),
    led: LED_COLS.map(c => makeHalo(70, c, 0.16, 1.5)), crown: makeHalo(10, '#9ac8ff', 0.2),
    lant: makeHalo(34, '#ffb050', 0.24, 1.6), lantBig: makeHalo(52, '#ffa848', 0.26, 1.6), lantCore: makeHalo(10, '#ffe0a0', 0.4),
    bulb: makeHalo(3, '#ffe0a0', 0.5, 1), inLamp: makeHalo(30, '#ffc070', 0.22), neon: makeHalo(16, '#ffb040', 0.3),
    nozzle: makeHalo(14, '#ffe0a0', 0.3), fount: makeHalo(90, '#ffd890', 0.12, 1.4), jetTop: makeHalo(20, '#fff2d8', 0.34), jetGlow: makeHalo(26, '#ffe6b8', 0.14),
    abraLamp: makeHalo(8, '#ffc860', 0.45), bbGlow: makeHalo(40, '#ff6a5a', 0.12, 1.5), arcade: makeHalo(28, '#ffae50', 0.16, 1.4),
    prom: makeHalo(5, '#ffd090', 0.35), station: makeHalo(40, '#bfe0ff', 0.14, 1.5), trainHead: makeHalo(8, '#fffbe8', 0.5),
    ember: makeHalo(8, '#ff7a2a', 0.5), floorPool: makeHalo(60, '#ff9a40', 0.12, 1.4), uplight: makeHalo(12, '#ffc070', 0.2),
    caravan: makeHalo(6, '#ffb050', 0.42, 1.4), camp: makeHalo(14, '#ff8a3a', 0.36, 1.5), campCore: makeHalo(5, '#ffd080', 0.5),
  };
  SKY_C = renderSky(); MOON_C = renderMoon(); HAZES = HAZE_DEFS.map(makeHaze);
  DUNE_BACK = renderDunes(DUNES.slice(0, 1)); DUNE_FRONT = renderDunes(DUNES.slice(1));
  CAMEL_C = [true, false].map(rider => CAMEL_LEGS.map(legs => makeCamel(rider, legs)));
  FAR = renderFar(); TWR = renderTowers(); BURJ = renderBurj();
  [LC, lx] = makeCanvas(BK_M.w, BK_M.h);
  VIA_C = renderViaduct(); STN_F = renderStationFront(); NEAR_C = renderNear();
  TRAIN_C = [sprite(TRAIN_ROWS, TRAIN_KEY), sprite(flipRows(TRAIN_ROWS), TRAIN_KEY)];
  // the hoarding hides the far towers' aircraft lamps behind it
  FAR.blinksBB = FAR.blinks.filter(([x, y]) => !(x >= BBX.l - 1 && x <= BBX.r + 1 && y >= BBX.t - 5 && y <= BB_BLD.top));
  BBF_C = renderBillboardFrame(); BB_AD = renderBillboardAd(false); BB_AD_HI = renderBillboardAd(true);
  BB_LINE_C = BB_LINES.map(renderBillboardLine);
  PITCH = makePitch(BB.w, BB.h, { accent: '#c77dff' });
  WATER_C = renderWater(); MIST_C = makeMist();
  [FC, fx] = makeCanvas(W, FB + 1);
  SHOOT_C = renderColumn(7, 96, '#ffffff', '#fff4dc', '#ffdca0', '#ffc860', 0.22); JG_C = renderJetGlow(); JET_C = renderJets(); RH = new Float32Array(RIP.length);
  ABRA_C = [sprite(ABRA_ROWS, ABRA_KEY), sprite(flipRows(ABRA_ROWS), ABRA_KEY)];
  TERR_C = renderTerrace(); SHISHA_C = renderShisha(); DALLAH_C = sprite(DALLAH_ROWS, DALLAH_KEY);
  LANT_C = [0, 1].map(s => [makeLantern(s, false), makeLantern(s, true)]); DOTS = LANTS.map(lanternDots);
  CAFE_C = renderCafe();
  PALM_LC = renderPalm(PALM_L, 5, 84); PALM_RC = renderPalm(PALM_R, 11, 440);
  CROWN_L = makeCrown(PALM_L, 300, FRONDS_L); CROWN_R = makeCrown(PALM_R, 400, FRONDS_R);
  CAT_C = makeCat();
  PUFF_C = [];
  for (let pr = 1; pr <= 3; pr++) {
    const s = pr * 2 + 1, p = pix(s, s);
    for (let y = 0; y < s; y++) for (let i = 0; i < s; i++) { const d = Math.hypot(i - pr, y - pr) / pr; if (d < 1 && (1 - d) * 1.7 > bayer(i, y) * 0.9) p.set(i, y, '#ece4e0'); }
    PUFF_C[pr] = p.done();
  }
  VIG_C = renderVignette();
}
function renderVignette() {
  const p = pix(W, H), c = [4, 3, 12];
  for (let y = 0; y < H; y++) for (let i = 0; i < W; i++) {
    const v = smooth(0.72, 1.45, Math.hypot((i - W / 2) / (W / 2), (y - H / 2) / (H / 2) * 0.9));
    const q = Math.floor(v * 4 + bayer(i, y)) / 4;
    if (q > 0) p.set(i, y, c, Math.round(q * 0.45 * 255));
  }
  return p.done();
}

/* ================= Per-frame: sky, skyline, the Burj ================= */
function drawSky(t) {
  ctx.drawImage(SKY_C, 0, 0);
  ctx.fillStyle = '#ece4f4';
  for (const s of STARS) { ctx.globalAlpha = s.b * (0.55 + 0.45 * wave(t, s.k, s.ph)); ctx.fillRect(s.x, s.y, 1, 1); }
  ctx.globalAlpha = 1;
  halo(HL.moon, MOON.x, MOON.y + 1, 0.9);
  ctx.drawImage(MOON_C, MOON.x - 6, MOON.y - 6);
  drawHaze(HAZES[2], t);
}
const CROWN_COLS = ['#7ab8ff', '#c77dff', '#ffd27a', '#6affd8', '#ff7ab8'];
function drawCrowns(t) {
  for (const c of CROWNS) {
    const col = c.twin ? (wave(t, 6, c.ph) > 0 ? '#eef6ff' : '#bfe0ff') : CROWN_COLS[(step(t, 8 * c.k) + Math.floor(c.ph * 5)) % CROWN_COLS.length];
    rect(ctx, c.x, c.y, c.w, c.twin ? 1 : 2, col);
    halo(HL.crown, c.x + c.w / 2, c.y + 1, 0.7);
  }
}
function drawBlinkers(t, board) {
  const I = blinkI(t);
  if (I <= 0.02) return;
  ctx.fillStyle = I > 0.5 ? '#ff4a3a' : '#b02020';
  for (const [x, y] of [...BURJ.blinks, ...TWR.blinks]) { ctx.fillRect(x, y, 1, 1); halo(HL.red, x, y, I); }
  for (const [x, y] of board ? FAR.blinksBB : FAR.blinks) { ctx.fillRect(x, y, 1, 1); halo(HL.red, x, y, I * 0.5); }
}
const SPARKLE = [80, 92];                             // on the hour: the whole tower twinkles with white strobes
function drawBurjGlow(t, st, lv) {
  halo(HL.bk, BK.cx, 112, 0.75 + 0.1 * wave(t, 8));
  if (st && lv > 0.01) halo(HL.led[st.main], BK.cx, 110, lv);
}
function drawBurjFx(t) {
  halo(HL.bkTop, BK.cx, 52, 0.8 + 0.2 * wave(t, 12));
  halo(HL.tip, BK.cx, BK.tip, 0.6);
  const gs = step(t, 2400), sp = env(t, SPARKLE[0], SPARKLE[1], 1.5), n = 8 + Math.round(sp * 70);
  ctx.fillStyle = '#ffffff';
  for (let k = 0; k < n; k++) {
    if (hash(k, gs, 61) > 0.3 + sp * 0.5) continue;
    const [x, y] = BK_SPARK[Math.floor(hash(k, gs, 62) * BK_SPARK.length)];
    ctx.fillRect(x, y, 1, 1);
  }
  if (sp > 0.05) halo(HL.bkTop, BK.cx, 100, sp * 0.6);
}

/* ================= Per-frame: the metro, the lakeside ================= */
function drawTrain(t) {
  const tr = trainX(t);
  if (!tr) return;
  const y = VIA - 7;
  ctx.drawImage(TRAIN_C[tr.dir > 0 ? 0 : 1], tr.x, y);
  halo(HL.trainHead, tr.dir > 0 ? tr.x + TR_W - 1 : tr.x, y + 4, 0.8);
}
function drawNearFx(t) {
  for (const [x, y] of PROM_LAMPS) halo(HL.prom, x, y, 0.8 + 0.15 * wave(t, 300, x / 50));
  for (const x of [48, 100, 150]) halo(HL.arcade, x, 170, 0.8 + 0.08 * wave(t, 90, x / 70));
  for (const { x } of TOWERS_W) halo(HL.uplight, x + 4, SOUK.roof - 3, 0.6);
  const s = step(t, 960);
  for (const w of WALKERS) {
    const u = frac(w.k * t / LOOP + w.ph), x = Math.round(w.dir > 0 ? lerp(4, 418, u) : lerp(418, 4, u)), leg = (s + w.k) & 1;
    rect(ctx, x, 171, 1, 1, '#1a1012'); rect(ctx, x, 172, 1, 2, w.col); rect(ctx, x + (w.dir > 0 ? 1 : -1), 172, 1, 1, w.col);
    rect(ctx, x - leg, 174, 1, 1, '#1a1012'); rect(ctx, x + 1 - leg, 174, 1, 1, '#1a1012');
  }
}

/* ================= Per-frame: the lake ================= */
function drawWater(t, board, st, lv) {
  const sl = showLevel(t);
  ctx.drawImage(WATER_C, 0, WL);
  reflect(ctx, t, { top: WL, rows: EDGE - WL, squash: 2, alpha: 0.55, k1: 40, k2: 96, amp: 0.3 + 0.9 * sl });
  rect(ctx, 0, WL, W, EDGE - WL, 'rgba(8,6,22,0.16)');
  rect(ctx, 0, WL, W, 1, '#0c0a1a');
  for (const s of STREAKS) streak(ctx, t, s.x, s.w, s.col, s.a, s.seed, WL + 1, EDGE);
  if (board) streak(ctx, t, BB.x + (BB.w >> 1), 10, '#ff8a6a', 0.14, 14, WL + 1, EDGE);   // the hoarding's glow
  if (st && lv > 0.01) streak(ctx, t, BK.cx, 12, LED_COLS[st.main], 0.32 * lv, 21, WL + 1, EDGE);
  if (sl > 0.01) for (let k = 0; k < 5; k++) streak(ctx, t, 100 + k * 70, 18, '#ffe0a8', 0.2 * sl, 30 + k, FY + 1, EDGE);
  const gs = step(t, 1440);
  for (let k = 0; k < 36; k++) {
    if (hash(k, gs, 17) < 0.45) continue;
    const x = Math.floor(hash(k, gs, 18) * W), y = WL + 3 + Math.floor(hash(k, gs, 19) * (EDGE - WL - 5));
    const warm = STREAKS.some(s => Math.abs(s.x - x) < s.w + 3);
    ctx.globalAlpha = warm ? 0.7 : 0.3; ctx.fillStyle = warm ? '#ffe8c0' : '#8a86b8';
    ctx.fillRect(x, y, warm ? 2 : 1, 1);
  }
  ctx.globalAlpha = 1;
}

/* ================= Per-frame: the hoarding ================= */
const BB_RING = [];
for (let x = BB.x - 1; x <= BB.x + BB.w; x++) BB_RING.push([x, BB.y - 1]);
for (let y = BB.y; y < BB.y + BB.h; y++) BB_RING.push([BB.x + BB.w, y]);
for (let x = BB.x + BB.w; x >= BB.x - 1; x--) BB_RING.push([x, BB.y + BB.h]);
for (let y = BB.y + BB.h - 1; y >= BB.y; y--) BB_RING.push([BB.x - 1, y]);
function drawBillboardAd(t) {
  ctx.drawImage(BB_AD, BB.x, BB.y);
  const u = frac(t * 40 / LOOP);                       // a shine runs across KARAK every 6 s
  if (u < 0.25) {
    const sx = BB_TX - 4 + Math.round(u / 0.25 * 42), a = Math.max(BB_TX, sx), b = Math.min(BB_TX + 34, sx + 4);
    if (b > a) ctx.drawImage(BB_AD_HI, a, 2, b - a, 10, BB.x + a, BB.y + 2, b - a, 10);
  }
  const i = step(t, 48), A = BB_LINE_C[i % 3], B = BB_LINE_C[(i + 1) % 3], h = 6, ly = BB.y + 14;   // the line rolls every 5 s
  const s = Math.round(smooth(0.94, 1, frac(t * 48 / LOOP)) * h);
  if (s < h) ctx.drawImage(A, 0, s, A.width, h - s, BB.x + BB_TX, ly, A.width, h - s);
  if (s > 0) ctx.drawImage(B, 0, 0, B.width, s, BB.x + BB_TX, ly + h - s, B.width, s);
  ctx.fillStyle = '#fff4ea';                           // steam off the glass
  for (const [k, gx] of [[0, 3], [1, 6]]) for (let j = 1; j <= 6; j++) {
    const ph = k * 0.45, ks = 40 + k * 12;
    if (frac(ks * t / LOOP + ph - j * 0.2) < 0.22) continue;
    const sw = Math.round(Math.sin(j * 0.9 - TAU * (ks * t / LOOP + ph)) * Math.min(1.5, j * 0.4));
    ctx.globalAlpha = 0.85 * (1 - (j - 1) / 6);
    ctx.fillRect(BB.x + GLASS.x + gx + sw, BB.y + GLASS.y - j, 1, 1);
  }
  ctx.globalAlpha = 1;
}
function drawBillboard(t, pitch) {
  halo(HL.bbGlow, BB.x + (BB.w >> 1), BB.y + (BB.h >> 1), 0.9);
  blit(BBF_C);
  if (!PITCH.covers(t, pitch)) drawBillboardAd(t);
  PITCH.draw(ctx, t, BB.x, BB.y, pitch);
  const s = step(t, 1200);                              // chasing LED border
  for (let i = 0; i < BB_RING.length; i++) { const q = (i + s) % 6; ctx.fillStyle = q < 2 ? '#ffd27a' : q < 3 ? '#c77dff' : '#3a1a2a'; ctx.fillRect(BB_RING[i][0], BB_RING[i][1], 1, 1); }
  const I = blinkI(t);
  if (I > 0.02) { ctx.fillStyle = I > 0.5 ? '#ff4a3a' : '#b02020'; for (const [x, y] of BB_BEACONS) { ctx.fillRect(x, y, 1, 1); halo(HL.red, x, y, I); } }
}

/* ================= People on our terrace =================
   A family on the left cushions and two friends on the right ones, sitting cross-legged with their backs to us to
   watch the lake; a man smoking the shisha on a leather pouf; and a waiter who comes out from behind the café wall,
   along the back of the terrace, to pour qahwa for the majlis or to change the shisha's coals.
   Timeline (loop seconds):
     waiter    6-39 pours qahwa, 84-102 changes the coals, 122-155 pours again, 206-224 the coals again
     smoker    draws on the shisha at SMOKE_DRAWS (the coals glow up), and breathes out a cloud a moment later;
               after each coal change he takes a long draw
     shows     the mother films both fountain shows; the child throws up his arms at each finale (67.6, 192.6);
               the woman on the right points at the first shooter (55, 180)
     always    sips of qahwa and karak, heads turning to a neighbour, on periods that divide the loop */
const R = (x, y, w, h, c) => { ctx.fillStyle = c; ctx.fillRect(x, y, w, h); };
const NIGHT = '#150e24', GLOWC = '#ffe6c0';
/** a sitter's colours, mixed towards the night, with a line of the lake's light along the shoulders */
function folk(top, bot, skin, hair, o = {}) {
  const m = c => mixHex(c, NIGHT, o.dk ?? 0.22);
  return { top: m(top), topD: mixHex(top, NIGHT, 0.5), topL: mixHex(top, GLOWC, 0.35), bot: m(bot), botD: mixHex(bot, NIGHT, 0.5),
    skin: m(skin), hair: m(hair), hairL: mixHex(hair, GLOWC, 0.3), long: o.long || 0, child: !!o.child, wear: o.wear || null, trim: o.trim || null };
}
const SKIN = ['#f2c8a0', '#e2ac82', '#c48c60', '#8c5a3c'];
const SIT_Y = 243;                                   // the box cushions' top row
const FOLK = {
  father: folk('#e4d8c4', '#e4d8c4', SKIN[2], '#1c1412', { wear: 'ghutra', dk: 0.16 }),
  child: folk('#e8823a', '#33466e', SKIN[2], '#1c1412', { child: true }),
  mother: folk('#2a2030', '#2a2030', SKIN[1], '#1a141c', { wear: 'shayla', trim: '#d8a848', dk: 0.1 }),
  friendA: folk('#2a9a8a', '#33466e', SKIN[0], '#3c2418'),
  friendB: folk('#e8c040', '#22202c', SKIN[1], '#1c1412', { long: 4 }),
  smoker: folk('#6a7a4a', '#4a3a30', SKIN[3], '#141012'),
};
/**
 * One person sitting with their back to us, the seat on row sy, centred on column x (the head turned lean px).
 * arms: [left, right], each 0 down, 1 straight up, 2 holding a phone up, 3 a cup or the hose to the mouth.
 * pouf: sitting on a pouf, knees forward (a narrower lap).
 */
function sitter(x, sy, P, { arms = [0, 0], lean = 0, pouf = false, cup = '#e8dcc8' } = {}) {
  x = Math.round(x);
  const kid = P.child, th = kid ? 6 : 10, tw = kid ? 6 : 9, hh = kid ? 5 : 6, hw = kid ? 4 : 5, kn = pouf ? 5 : kid ? 4 : 7, lap = kid ? 2 : 3;
  const ty = sy - lap + 1 - th, hy = ty - hh, tx = x - (tw >> 1);
  R(x - kn, sy - lap + 1, kn * 2 + 1, lap, P.bot); R(x - kn, sy - lap + 2, 1, lap - 1, P.botD); R(x + kn, sy - lap + 2, 1, lap - 1, P.botD);   // the lap, knees out
  R(tx + 1, ty, tw - 2, 1, P.topL); R(tx, ty + 1, tw, th - 1, P.top); R(tx, ty + 1, 1, 1, P.topL); R(tx + tw - 1, ty + 1, 1, th - 1, P.topD);
  if (P.trim) R(tx, ty + th - 1, tw, 1, P.trim);                                          // the abaya's embroidered hem
  arms.forEach((st, k) => {
    const s = k ? 1 : -1, ax = s < 0 ? tx - 1 : tx + tw;
    if (st === 1) { R(ax, hy - 3, 1, ty - hy + 5, P.topD); R(ax, hy - 4, 1, 1, P.skin); }
    else if (st === 2) {
      R(ax, hy + 1, 1, ty - hy + 2, P.topD); R(ax, hy, 1, 1, P.skin);
      const p0 = s < 0 ? ax - 2 : ax - 1;
      R(p0, hy - 4, 4, 4, '#0e1018'); R(p0 + 1, hy - 3, 2, 1, '#fff0d0'); R(p0 + 1, hy - 2, 2, 1, '#8ab8e0');   // the screen, filming the show
    } else if (st === 3) { R(ax, ty - 1, 1, 5, P.topD); R(ax - s, hy + hh - 2, 1, 3, P.skin); if (cup) R(ax, hy + hh - 2, 1, 2, cup); }
    else { R(ax, ty + 1, 1, th - 2, P.topD); R(ax, ty + th - 1, 1, 1, P.skin); }
  });
  const hx = x - (hw >> 1) + lean;
  if (P.wear === 'ghutra') {                         // a white headcloth with the black agal, draped over the shoulders
    R(hx - 1, hy, hw + 2, hh, '#f4f2ee'); R(hx - 1, hy + hh, hw + 2, 5, '#e2e0dc'); R(hx + hw, hy + 1, 1, hh + 4, '#a8a4ac');
    R(hx, hy - 1, hw, 1, '#141018'); R(hx - 1, hy, 1, 1, '#141018'); R(hx + hw, hy, 1, 1, '#141018');   // the agal's ring on the crown
    R(hx - 1, hy + 1, hw + 1, 1, '#fffcf6');
  } else if (P.wear === 'shayla') {                  // a dark shayla wrapped over the head and shoulders, a gold edge
    R(hx, hy - 1, hw, 1, P.hairL); R(hx - 1, hy, hw + 2, hh + 1, P.hair); R(hx - 1, hy, 1, hh, P.hairL);
    R(hx - 2, hy + hh + 1, hw + 4, 2, P.hair); R(hx - 2, hy + hh + 2, 1, 1, P.trim); R(hx + hw + 1, hy + hh + 2, 1, 1, P.trim);
  } else {
    R(hx + 1, hy, hw - 2, 1, P.hairL); R(hx, hy + 1, hw, hh - 1, P.hair); R(hx, hy + 1, 1, 1, P.hairL); R(hx + hw - 1, hy + 1, 1, 1, P.hairL);
    if (P.long) { R(hx, hy + hh, hw, P.long, P.hair); R(hx, hy + hh, 1, P.long, P.hairL); }
    else { R(hx + 1, hy + hh - 1, hw - 2, 1, P.skin); R(hx - 1, hy + 2, 1, 2, P.skin); R(hx + hw, hy + 2, 1, 2, P.skin); }   // the nape, the ears
  }
  if (lean) R(lean > 0 ? hx + hw : hx - 1, hy + 2, 1, 3, P.skin);                        // a cheek, turned to a neighbour
  return hy;
}
const SITTERS = [                                    // x, the look, a phase for their little habits
  { x: 111, P: FOLK.father, ph: 0.1, to: 1 }, { x: 125, P: FOLK.child, ph: 0.55, to: -1 }, { x: 138, P: FOLK.mother, ph: 0.3, to: -1 },
  { x: 224, P: FOLK.friendA, ph: 0.7, to: 1 }, { x: 244, P: FOLK.friendB, ph: 0.2, to: -1 },
];
const FINALES = SHOWS.map(S => S.a + S.secs.find(s => s[0] === 'finale')[1] + 0.1);
const FIRST_SHOTS = SHOWS.map(S => S.a + S.secs.find(s => s[0] === 'shoot')[1]);
const sipping = (t, per, ph) => frac(t / per + ph) < 2.6 / per;
function drawSitters(t) {
  const lv = showLevel(t);
  SITTERS.forEach((c, k) => {
    let arms = [0, 0], lean = frac(t / 30 + c.ph) < 0.14 ? c.to : 0, cup;
    if (k === 0) { if (sipping(t, 24, c.ph)) { arms = [0, 3]; cup = '#e8c060'; } }                       // a finjan of qahwa
    else if (k === 1) { if (FINALES.some(a => t >= a && t < a + 2.8)) arms = [1, 1]; else if (lv > 0.5) lean = frac(t / 6 + c.ph) < 0.3 ? 1 : 0; }
    else if (k === 2) { if (lv > 0.6 && frac(t / 40 + 0.15) > 0.12) { arms = [0, 2]; lean = 0; } }
    else if (k === 3) { if (sipping(t, 16, c.ph)) arms = [0, 3]; }
    else { if (FIRST_SHOTS.some(a => t >= a && t < a + 2.4)) arms = [1, 0]; else if (sipping(t, 20, c.ph)) arms = [3, 0]; }
    if (k >= 3 && frac(t / 24 + 0.4) < 0.18) lean = c.to;                                               // the friends chat
    sitter(c.x, SIT_Y, c.P, { arms, lean, cup });
  });
}

/* the smoker on his pouf: a draw on the hose brightens the coals, then he breathes out a slow cloud that drifts off */
const SMOKE_DRAWS = [8, 29, 50, 71, 98, 117, 139, 160, 181, 220];
const DRAW_LEN = 2.4, EXHALE = 4.6;
const lastDraw = t => { let a = -99; for (const d of SMOKE_DRAWS) if (d <= t) a = d; return a; };
/** how hard the coals glow from the smoker's draws and the waiter's fresh coals, 0..1 */
function coalBoost(t) {
  const d = t - lastDraw(t), draw = d < DRAW_LEN ? smooth(0, 0.6, d) : 1 - smooth(DRAW_LEN, DRAW_LEN + 1.2, d);
  let fresh = 0;
  for (const c of COAL_TIMES) fresh = Math.max(fresh, env(t, c, c + 14, 2));
  return Math.max(draw * 0.8, fresh);
}
function drawSmoker(t, gust) {
  const d = t - lastDraw(t), drawing = d >= 0 && d < DRAW_LEN, P = FOLK.smoker;
  const hy = sitter(POUF.x, POUF.y, P, { arms: [drawing ? 3 : 0, 0], pouf: true, cup: null });
  if (drawing) { ctx.fillStyle = '#3a2a3a'; for (let y = hy + 7; y <= HOSE_END[1]; y++) ctx.fillRect(Math.round(lerp(POUF.x - 5, HOSE_END[0], (y - hy - 7) / (HOSE_END[1] - hy - 7))), y, 1, 1); }
  const e = d - DRAW_LEN - 0.5;                      // the exhale: puffs swelling and drifting with the breeze
  if (e > 0 && e < EXHALE) {
    const u = e / EXHALE;
    for (let k = 0; k < 6; k++) {
      const q = clamp(u * 1.4 - k * 0.07, 0, 1);
      if (q <= 0) continue;
      const pr = Math.min(3, 1 + Math.floor(q * 3)), x = POUF.x + 1 + Math.round(q * (10 + k * 2) + gust * 5 * q + wave(t, 30 + k * 7, k * 0.2) * 1.5);
      ctx.globalAlpha = 0.6 * (1 - q) * Math.min(1, q * 8);
      ctx.drawImage(PUFF_C[pr], x - pr, Math.round(hy + 1 - q * (12 + k * 2)) - pr);
    }
    ctx.globalAlpha = 1;
  }
}

/* the waiter: a white shirt, a burgundy waistcoat, along the back of the terrace (behind the shisha and the majlis) */
const WAIT_Y = 236;                                  // the row his feet walk on, between the ledge and the rug
const WAIT_FROM = 432;                               // out of sight behind the café wall
const WAIT_V = 20;                                   // px per second
const POUR_X = CUPS.map(x => x + 12).reverse();      // where he stands to pour into each finjan, right to left
const COAL_X = SHISHA.x + 9;                         // where he stands to change the coals
const WAITER = [{ a: 6, kind: 'pour' }, { a: 84, kind: 'coals' }, { a: 122, kind: 'pour' }, { a: 206, kind: 'coals' }];
WAITER.forEach(w => {
  w.at = w.kind === 'pour' ? POUR_X[0] : COAL_X;
  w.walk = (WAIT_FROM - w.at) / WAIT_V;
  w.job = w.kind === 'pour' ? 7.6 : 5.4;
  w.b = w.a + w.walk * 2 + w.job;
});
const COAL_TIMES = WAITER.filter(w => w.kind === 'coals').map(w => w.a + w.walk + 1.2);   // when the fresh coals go on
const WAITER_P = { skin: mixHex(SKIN[2], NIGHT, 0.2), hair: '#1c1412', shirt: '#e4ded2', vest: '#6a1a26', vestL: '#a84a50', bot: '#1e1c28', shoe: '#0e0c12' };
const DALLAH_S = ['..y..', '.yBd.', 'b.BD.', '.bBBd', '.yBBd', '..DD.'];   // his own small brass dallah, the spout to the left
const DALLAH_SK = { y: '#ffe08a', B: '#c8902e', D: '#7a4e1c', d: '#5a3a18', b: '#e8b050' };
function smallDallah(x, y) {
  DALLAH_S.forEach((row, j) => { for (let i = 0; i < row.length; i++) { const c = DALLAH_SK[row[i]]; if (c) R(x + i, y + j, 1, 1, c); } });
}
/** the waiter at (x, his feet on WAIT_Y): walking in profile (dir, stride) or facing us at a job */
function waiterFigure(x, dir, stride, facing) {
  const P = WAITER_P, fy = WAIT_Y, legs = 11, th = 11, hh = 6, ty = fy - legs - th + 1, hy = ty - hh, ly = fy - legs + 1;
  ctx.globalAlpha = 0.4; R(x - 4, fy + 1, 9, 1, '#06040a'); ctx.globalAlpha = 1;          // his shadow on the tiles
  if (facing) {
    const tx = x - 4;
    R(tx, ly, 8, 2, P.bot); R(tx, ly + 2, 3, legs - 3, P.bot); R(tx + 5, ly + 2, 3, legs - 3, P.bot); R(tx + 7, ly, 1, legs - 1, '#100e16');
    R(tx, fy, 3, 1, P.shoe); R(tx + 5, fy, 3, 1, P.shoe);
    R(tx, ty, 8, th, P.vest); R(tx, ty, 8, 1, P.vestL); R(tx + 7, ty + 1, 1, th - 1, '#4a121c');
    R(x - 2, ty, 4, 1, P.shirt); R(x - 1, ty + 1, 2, 2, P.shirt); R(x - 1, ty + 4, 1, 1, '#d8b060'); R(x - 1, ty + 7, 1, 1, '#d8b060');   // collar, buttons
    R(x - 2, hy, 5, hh, P.skin); R(x - 2, hy, 5, 2, P.hair); R(x - 1, hy + 3, 1, 1, '#2a1a14'); R(x + 1, hy + 3, 1, 1, '#2a1a14');
    R(x - 1, hy + 4, 3, 1, '#4a2a1c');                                                     // his moustache
    return { ty, hy };
  }
  if (stride) {
    R(x - 2, ly, 5, 2, P.bot);
    R(dir > 0 ? x + 1 : x - 2, ly + 2, 2, legs - 2, P.bot); R(dir > 0 ? x + 1 : x - 3, fy, 3, 1, P.shoe);
    R(dir > 0 ? x - 2 : x + 1, ly + 2, 2, legs - 3, '#16141e'); R(dir > 0 ? x - 2 : x + 1, fy - 1, 2, 1, P.shoe);
  } else { R(x - 1, ly, 3, legs, P.bot); R(dir > 0 ? x - 1 : x - 2, fy, 4, 1, P.shoe); }
  R(x - 3, ty, 6, th, P.vest); R(x - 3, ty, 6, 1, P.vestL); R(dir > 0 ? x + 2 : x - 3, ty, 1, 3, P.shirt);
  R(x - 2, hy, 5, hh, P.skin); R(x - 2, hy, 5, 2, P.hair); R(dir > 0 ? x - 2 : x + 2, hy, 1, hh - 1, P.hair);
  R(dir > 0 ? x + 1 : x - 1, hy + 3, 1, 1, '#2a1a14'); R(dir > 0 ? x + 2 : x - 2, hy + 4, 1, 1, '#4a2a1c');   // an eye, the moustache
  return { ty, hy };
}
function drawWaiter(t) {
  const w = WAITER.find(q => t >= q.a && t < q.b);
  if (!w) return;
  const s = t - w.a, back = w.a + w.walk + w.job, stride = step(t, 960) & 1, P = WAITER_P;
  if (s < w.walk || t >= back) {                     // walking, the pot or the coal pan in his front hand
    const out = s < w.walk, x = Math.round(out ? lerp(WAIT_FROM, w.at, s / w.walk) : lerp(w.at, WAIT_FROM, (t - back) / w.walk)), dir = out ? -1 : 1;
    R(dir > 0 ? x - 4 : x + 3, WAIT_Y - 20, 1, 7, '#b8b0a8');                                // the far arm, behind him
    const { ty } = waiterFigure(x, dir, stride, false), hx = x + dir * 4;
    R(dir > 0 ? x + 1 : x - 3, ty + 2, 3, 1, P.shirt); R(hx, ty + 2, 1, 3, P.shirt); R(hx, ty + 5, 1, 1, P.skin);   // the arm held out
    if (w.kind === 'pour') smallDallah(dir > 0 ? hx - 1 : hx - 3, ty + 6);
    else { R(hx - 1, ty + 6, 3, 2, '#1a1418'); R(hx - 1, ty + 6, 3, 1, hash(1, step(t, 1440), 2) < 0.5 ? '#ff8a30' : '#ffb050'); halo(HL.ember, hx, ty + 6, 0.6); }
    return;
  }
  const j = s - w.walk;                              // at the job, facing us
  if (w.kind === 'pour') {
    const k = Math.min(2, Math.floor(j / 2.5)), v = j - k * 2.5, x = POUR_X[k] - (v > 2.1 && k < 2 ? 3 : 0);
    const { ty } = waiterFigure(x, -1, 0, true), hx = x - 7, hy = ty + 13 - (v < 0.4 ? 0 : 2);
    for (let y = ty + 1; y <= hy; y++) R(Math.round(lerp(x - 5, hx, (y - ty - 1) / (hy - ty - 1))), y, 1, 1, P.shirt);   // his arm out over the cup
    R(hx, hy, 1, 1, P.skin);
    smallDallah(hx - 4, hy - 1);
    if (v > 0.5 && v < 1.9) { R(hx - 4, hy + 2, 1, TABLE.y - 3 - hy - 1, '#8a4a1c'); R(hx - 4, hy + 2, 1, 1, '#e8b050'); }   // the thin stream of qahwa
    R(x + 4, ty + 1, 1, 7, P.shirt); R(x + 4, ty + 8, 1, 1, P.skin);                        // the other hand at his side
  } else {
    const { ty } = waiterFigure(COAL_X, -1, 0, true), hx = COAL_X - 6, hy = ty + 9;
    for (let y = ty + 1; y <= hy; y++) R(Math.round(lerp(COAL_X - 5, hx, (y - ty - 1) / (hy - ty - 1))), y, 1, 1, P.shirt);
    R(hx, hy, 1, 1, P.skin);
    const reach = j > 1 && j < 4.4, tip = reach ? [SHISHA.x + 1, SHISHA.top - 1] : [hx - 1, hy + 4];
    ctx.fillStyle = '#8a8478'; for (let q = 1; q <= 4; q++) ctx.fillRect(Math.round(lerp(hx, tip[0], q / 4)), Math.round(lerp(hy, tip[1], q / 4)), 1, 1);   // the tongs
    R(COAL_X + 4, ty + 1, 1, 6, P.shirt); R(COAL_X + 3, ty + 7, 4, 2, '#1a1418');           // the coal pan at his other hand
    R(COAL_X + 3, ty + 7, 4, 1, hash(1, step(t, 1440), 2) < 0.5 ? '#ff8a30' : '#ffb050'); halo(HL.ember, COAL_X + 5, ty + 7, 0.7);
  }
}

/* ================= Per-frame: our terrace ================= */
function steam(x, y, n, k0, rise, t, a, drift, big) {
  for (let k = 0; k < n; k++) {
    const u = frac(t * (k0 + k * 5) / LOOP + k * 0.23), X = x + Math.round(wave(t, 30 + k * 4, k * 0.1) * 1.5 * u + u * drift);
    ctx.globalAlpha = a * Math.min(1, u * 6) * (1 - u);
    const pr = big ? 2 + (u > 0.5 ? 1 : 0) : 1 + (u > 0.5 ? 1 : 0);
    ctx.drawImage(PUFF_C[pr], X - pr, Math.round(y - u * rise) - pr);
  }
  ctx.globalAlpha = 1;
}
function drawTerrace(t, gust) {
  blit(TERR_C);
  halo(HL.floorPool, 84, 252, flick(t, 0) * 0.9); halo(HL.floorPool, 268, 250, flick(t, 1) * 0.6);
  drawCat(t);                                                                       // on the ledge, behind everyone
  drawWaiter(t);                                                                    // along the back of the terrace
  blit(SHISHA_C);
  ctx.drawImage(DALLAH_C, DALLAH.x, DALLAH.y);
  steam(DALLAH.x + 1, DALLAH.y + 5, 4, 44, 16, t, 0.5, -3 + gust * 4, false);      // off the spout
  for (const [k, x] of CUPS.entries()) steam(x + 1, TABLE.y - 3, 2, 36 + k * 4, 10, t, 0.3, gust * 3, false);
  // the shisha's coal breathes, glowing up while it is drawn on or freshly changed; smoke curls up and leans with the breeze
  const cb = coalBoost(t), em = 0.65 + 0.2 * wave(t, 45, 0.2) + 0.15 * wave(t, 131, 0.7) + 0.35 * cb;
  rect(ctx, SHISHA.x - 1, SHISHA.top - 1, 3, 1, em > 0.8 ? '#ffb060' : '#e0602a'); rect(ctx, SHISHA.x, SHISHA.top - 2, 1, 1, cb > 0.5 ? '#ffd890' : '#ff8a3a');
  halo(HL.ember, SHISHA.x, SHISHA.top - 1, em);
  steam(SHISHA.x, SHISHA.top - 3, 5, 26, 34, t, 0.32 + 0.16 * cb, 6 + gust * 8, true);
  const mc = 0.7 + 0.3 * wave(t, 53, 0.4);                                         // the mabkhara's coal and its thread of smoke
  rect(ctx, MAB.x - 1, MAB.y - 1, 3, 1, mc > 0.85 ? '#ffb060' : '#e0602a'); rect(ctx, MAB.x, MAB.y - 2, 1, 1, mc > 0.8 ? '#ffd890' : '#ff8a3a');
  halo(HL.ember, MAB.x, MAB.y - 1, mc * 0.8);
  steam(MAB.x, MAB.y - 3, 4, 22, 40, t, 0.26, 4 + gust * 10, false);
  drawSitters(t); drawSmoker(t, gust);
  drawLanterns(t, l => l.id < 2);
  blit(CAFE_C); drawCafe(t);
  drawLanterns(t, l => l.id === 2);
  blit(PALM_LC.c); drawBulbs(PALM_LC.bulbs, t, 0);
  blit(PALM_RC.c); drawBulbs(PALM_RC.bulbs, t, 50);
  drawCrown(CROWN_L, t, gust); drawCrown(CROWN_R, t, gust);
  drawLanterns(t, l => l.id === 3);
}

/* ================= Frame compositor (back to front) ================= */
// pitch: the "your ad here" face (promo captures); billboard: false leaves the hoarding out (the loop videos);
// weather: a WEATHERS pick (site/js/engine/weather.js), the city's own when left out
function renderFrame(t, { pitch, billboard = true, weather, reducedMotion = false } = {}) {
  t = ((t % LOOP) + LOOP) % LOOP;
  ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over'; ctx.imageSmoothingEnabled = false;
  const wx = WEATHER(weather), lights = billboard ? LIGHTS_BB : LIGHTS, wxc = billboard ? WX_BB : WX;
  const gust = gustAt(t), lv = ledLevel(t), st = ledState(t);
  drawSky(t);
  drawPlane(t);
  drawDunes(t, gust);
  drawLayer(FAR, t, 7);
  drawHaze(HAZES[0], t);
  if (wx.sand) drawSand(SAND[0], t, lights, gust);
  drawWeather(ctx, wx.shared, 0, t, wxc, reducedMotion);
  drawLayer(TWR, t, 13);
  drawCrowns(t);
  if (billboard) drawBillboard(t, pitch);
  drawBurjGlow(t, st, lv);
  drawLayer(BURJ, t, 29);
  drawBurjShow(st, lv, t);
  drawBurjFx(t);
  drawBlinkers(t, billboard);
  drawHaze(HAZES[1], t, 0.75);
  blit(VIA_C);
  drawTrain(t);
  blit(STN_F); halo(HL.station, STN_MID, VIA - 6, 0.8);
  blit(NEAR_C);
  drawNearFx(t);
  drawWater(t, billboard, st, lv);
  drawFountainLayer(t, drawFountain(t));
  drawAbra(t);
  drawMist(t);
  if (wx.sand) drawSand(SAND[1], t, lights, gust);
  drawWeather(ctx, wx.shared, 1, t, wxc, reducedMotion);
  drawTerrace(t, gust);
  if (wx.sand) drawSand(SAND[2], t, lights, gust);
  drawWeather(ctx, wx.shared, 2, t, wxc, reducedMotion);
  ctx.drawImage(VIG_C, 0, 0);
}

export default {
  id: 'dubai',
  name: 'Dubai',
  tagline: 'Fountain night at the Burj',
  timeZone: 'Asia/Dubai',
  accent: '#c77dff',
  focusX: BK.cx,
  landmark: [BK.cx - 15, BK.cx + 15],                  // the Burj Khalifa
  landmarkTop: BK.tip,                                 // the row of the spire's tip, which landscape crops keep
  billboard: BB,
  ambience: { water: 0.35, crowd: 0.25, wind: 0.2, traffic: 0.15 },
  events: [
    ...TRAINS.map(tr => ({ t: tr.a, type: 'train', dur: TR_IN + TR_DWELL + TR_OUT, pan: tr.dir > 0 ? [-0.3, 0.75] : [0.75, -0.3], gain: 0.45 })),
    ...SHOWS.map(s => ({ t: s.a, type: 'chime' })),
    ...SPLASHES.map(s => ({ t: s.t, type: 'splash', x: s.x, gain: s.gain || 0.7 })),
    ...ABRAS.map(b => ({ t: b.a + 6, type: 'boat', dur: b.b - b.a - 12, pan: b.x1 > b.x0 ? [-0.9, 0.8] : [0.8, -0.9], gain: 0.25 })),
  ].sort((a, b) => a.t - b.t),
  create(c) {
    ctx = c;
    ctx.imageSmoothingEnabled = false;
    prerender();
    return { render: renderFrame };
  },
};
